import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { getPartner, servicePricing, getLabTests, getLabPackagesName, addLabBulk } from "../../../../Service/partnerAPI";
import Loader from "../../../../reusableUI/loader";
import { InputNumber, message, Table, Select, Input, AutoComplete, Popover, Switch, Upload, Dropdown } from "antd";
import { Plus, X, Pencil, Check, Ban, Info, Upload as UploadIcon, ChevronDown } from "lucide-react";
import ToggleTabs from "../../../../reusableUI/tap";
import secureLocalStorage from "react-secure-storage";
import { getLookupData } from "../../../../Service/lookup";
import { capitalizeFirstLetter } from "../../../../utils/Functions";
import * as XLSX from "xlsx";

const ONBOARDING_FIRST_KEY = "pricingOnboardingFirst";
const MAX_FEE = 99999;
const PHYSIO_WRAPPER_KEY = "physioService";
const ASSUME_DB_RETURNS_NET_FEE = true;

const LAB_SERVICE_KEY = "labService";
const LAB_PACKAGES_KEY = "labPackages";

const ALL_SERVICE_TABS = [
  { label: "Nurse", value: "Nurse" },
  { label: "Physiotherapist", value: "Physiotherapist" },
  { label: "Laboratory", value: "Laboratory" },
];

const SUPPORTED_SECTIONS = new Set(["Nurse", "Physiotherapist", "Laboratory"]);

const ONE_TIME_EXCLUDE_SERVICES = [
  "Tracheostomy",
  "Continuous Ryle's Tube Aspiration",
  "Hourly Ryle's Tube Aspiration",
];

const NURSE_ROLES = [
  { label: "Nurse", value: "nurse" },
  { label: "Nursing Assistant", value: "nursing-assistant" },
];

const SHIFT_DURATION_OPTIONS = [
  { label: "Morning 9H", value: "Morning|9H", shiftType: "Morning", duration: "9H" },
  { label: "Morning 12H", value: "Morning|12H", shiftType: "Morning", duration: "12H" },
  // label: "Night 9H", value: "Night|9H", shiftType: "Night", duration: "9H" },
  { label: "Night 12H", value: "Night|12H", shiftType: "Night", duration: "12H" },
  { label: "24H", value: "24H|24H", shiftType: "24H", duration: "24H" },
];

const labelCls = "text-xs font-semibold text-gray-600 mb-1 leading-tight block";
const fieldWrap = "flex flex-col min-w-0 w-full";

/* ---------------- Fasting boolean helper ---------------- */
const normalizeFastingBool = (v) => {
  if (v === true || v === false) return v;
  const s = String(v ?? "").trim().toLowerCase();
  if (!s || s === "0" || s === "false" || s === "no" || s === "null" || s === "undefined") return false;
  return true;
};

/* ---------------- Utils ---------------- */
const uuid = () => {
  if (typeof window !== "undefined" && window.crypto?.randomUUID) return window.crypto.randomUUID();
  return `${Date.now()}-${Math.random().toString(16).slice(2)}`;
};

const shiftFromShiftType = (shiftType) => {
  if (shiftType === "Morning") return "morning";
  if (shiftType === "Night") return "night";
  if (shiftType === "24H") return "24/7";
  return "";
};

const shiftTypeFromShift = (shift) => {
  if (shift === "morning") return "Morning";
  if (shift === "night") return "Night";
  if (shift === "24/7") return "24H";
  return shift || "";
};

const safeArr = (v) => (Array.isArray(v) ? v : []);
const toNumber = (value) => {
  const n = Number(value);
  return Number.isFinite(n) ? n : 0;
};
const roundCurrency = (value) => Number(toNumber(value).toFixed(2));
const pickFee = (obj) => toNumber(obj?.fee ?? obj?.fees ?? 0);

const clampInt = (v, { min = 0, max = MAX_FEE } = {}) => {
  if (v === null || v === undefined || v === "") return null;
  const n = Number(v);
  if (!Number.isFinite(n)) return null;
  const i = Math.trunc(n);
  return Math.min(max, Math.max(min, i));
};

const normalizeDiscount = (discount) => {
  if (!discount) return null;
  if (typeof discount === "object" && discount?.type && discount?.value != null) {
    const t = discount.type === "fixed" ? "fixed" : "percentage";
    const v = Number(discount.value);
    if (!Number.isFinite(v) || v <= 0) return null;
    return { type: t, value: v };
  }
  if (typeof discount === "string") {
    const str = discount.trim();
    if (!str) return null;
    if (str.includes("%")) {
      const v = Number(str.replace("%", "").trim());
      if (!Number.isFinite(v) || v <= 0) return null;
      return { type: "percentage", value: v };
    }
    const numeric = Number(str.replace(/[^\d.]/g, ""));
    if (Number.isFinite(numeric) && numeric > 0) return { type: "fixed", value: numeric };
  }
  return null;
};



const computeFinalFee = (baseFee, discountObj) => {
  const fee = Number(baseFee || 0);
  if (!discountObj || !fee) return fee;
  const v = Number(discountObj.value || 0);
  if (!Number.isFinite(v) || v <= 0) return fee;
  let final = fee;
  if (discountObj.type === "percentage") {
    const pct = Math.min(Math.max(v, 0), 100);
    final = fee - (fee * pct) / 100;
  } else {
    final = fee - v;
  }
  return roundCurrency(Math.max(0, final));
};

const invertDiscount = (netFee, discountObj) => {
  const n = Number(netFee || 0);
  if (!discountObj || !n) return n;
  if (discountObj.type === "percentage") {
    const pct = Math.min(Math.max(Number(discountObj.value || 0), 0), 100);
    const factor = 1 - pct / 100;
    if (factor <= 0) return n;
    return roundCurrency(n / factor);
  }
  const v = Number(discountObj.value || 0);
  if (!Number.isFinite(v) || v < 0) return n;
  return roundCurrency(n + v);
};

const formatDiscount = (discountObj) => {
  if (!discountObj) return "--";
  const v = Number(discountObj.value || 0);
  if (!Number.isFinite(v) || v <= 0) return "--";
  return discountObj.type === "percentage" ? `${v}%` : `₹${v}`;
};

const normalizeServiceName = (v) => String(v || "").trim().replace(/\s+/g, " ");

const isSameService = (a, b) => normalizeServiceName(a).toLowerCase() === normalizeServiceName(b).toLowerCase();
const isGeneralService = (name) => isSameService(name, "General Services");
const isExcludedOneTimeService = (name) => ONE_TIME_EXCLUDE_SERVICES.some((x) => isSameService(name, x));

const normalizeRole = (v) => {
  const s = String(v || "").trim().toLowerCase();
  if (!s) return "";
  if (s === "nurse") return "Nurse";
  if (s === "nursing assistant" || s === "nursingassistant" || s === "assistant") return "Nursing Assistant";
  return v;
};

const uniqTrimmed = (arr) => {
  const out = [];
  const seen = new Set();
  for (const x of safeArr(arr)) {
    const v = normalizeServiceName(x);
    if (!v) continue;
    const k = v.toLowerCase();
    if (seen.has(k)) continue;
    seen.add(k);
    out.push(v);
  }
  return out;
};

const extractPartnerServices = (partnerData) => {
  const raw = safeArr(partnerData?.services);
  return raw
    .map((item) => {
      if (typeof item === "string") return item;
      return item?.name || item?.label || item?.serviceType || item?.title || "";
    })
    .map((x) => String(x || "").trim())
    .filter(Boolean);
};

const readOnboardingFirst = () => {
  const v = secureLocalStorage.getItem(ONBOARDING_FIRST_KEY);
  return v === true || v === "true" || v === "1" || v === 1;
};

const resolvePricingSource = (pricingData) => {
  if (!pricingData || typeof pricingData !== "object") return null;
  if (pricingData?.pricingInfo && typeof pricingData.pricingInfo === "object") return pricingData.pricingInfo;
  return pricingData;
};

/* ---------------- Lab pricing extractors (bind getPartner labService.tests) ---------------- */
const extractLabTestsFromPricing = (pricingLike) => {
  const src = resolvePricingSource(pricingLike) || pricingLike;
  if (Array.isArray(src?.labService?.tests)) return src.labService.tests; // ✅ current shape
  if (Array.isArray(src?.labService)) return src.labService; // older shape
  if (Array.isArray(src?.tests)) return src.tests;
  return [];
};

const extractLabPackagesFromPricing = (pricingLike) => {
  const src = resolvePricingSource(pricingLike) || pricingLike;
  if (Array.isArray(src?.labService?.packages)) return src.labService.packages;
  if (Array.isArray(src?.labService?.labPackages)) return src.labService.labPackages;
  if (Array.isArray(src?.labPackages)) return src.labPackages;
  return [];
};

/* ---------------- NEW: normalize getLabPackagesName() response -> list of test names ---------------- */
const normalizeLabPackageTestsApi = (apiData) => {
  const rows = safeArr(apiData);

  // A) ["CBC", "LFT", ...]
  if (rows.length && rows.every((x) => typeof x === "string")) return uniqTrimmed(rows);

  // B) objects
  const out = [];
  for (const r of rows) {
    if (!r) continue;

    const single = r.parameterName || r.testName || r.name || r.title || r.label;
    if (single) out.push(single);

    const nested = safeArr(r.tests || r.parameters || r.items);
    for (const t of nested) {
      if (typeof t === "string") out.push(t);
      else if (t && typeof t === "object") {
        const nm = t.parameterName || t.testName || t.name || t.title || t.label;
        if (nm) out.push(nm);
      }
    }
  }
  return uniqTrimmed(out);
};

const hasAnyPricing = (src) => {
  if (!src) return false;

  const nurse = src?.nurseService;
  const physio = src?.physioService || src?.physioServices || src?.[PHYSIO_WRAPPER_KEY];

  const labTests = extractLabTestsFromPricing(src).length;
  const labPackages = extractLabPackagesFromPricing(src).length;

  const nurseRegular = safeArr(nurse?.regularShifts?.slots).length;
  const nurseOneTime = safeArr(nurse?.oneTimeShifts?.services).length;
  const physioServices = safeArr(physio?.services).length;
  const physioAssessment = toNumber(physio?.assessmentFees ?? physio?.assessmentFee ?? 0);

  return (
    nurseRegular > 0 ||
    nurseOneTime > 0 ||
    physioServices > 0 ||
    physioAssessment > 0 ||
    labTests > 0 ||
    labPackages > 0
  );
};

const buildTabsFromPricing = (src) => {
  const tabs = [];
  if (!src) return ALL_SERVICE_TABS;

  const nurse = src?.nurseService;
  const physio = src?.physioService || src?.physioServices || src?.[PHYSIO_WRAPPER_KEY];
  const doctor = src?.doctorService;

  const nurseHas =
    safeArr(nurse?.regularShifts?.slots).length > 0 || safeArr(nurse?.oneTimeShifts?.services).length > 0;

  const physioHas =
    safeArr(physio?.services).length > 0 || toNumber(physio?.assessmentFees ?? physio?.assessmentFee ?? 0) > 0;

  const doctorHas = safeArr(doctor?.services).length > 0;

  const labHas = extractLabTestsFromPricing(src).length > 0 || extractLabPackagesFromPricing(src).length > 0;

  if (nurseHas) tabs.push({ label: "Nurse", value: "Nurse" });
  if (physioHas) tabs.push({ label: "Physiotherapist", value: "Physiotherapist" });
  if (doctorHas) tabs.push({ label: "Doctor", value: "Doctor" });
  if (labHas) tabs.push({ label: "Laboratory", value: "Laboratory" });

  return tabs.length ? tabs : ALL_SERVICE_TABS;
};

const makeIdentity = (row) => {
  const kind = row?.kind;

  if (kind === "regular") {
    const role = normalizeRole(row.role);
    const serviceName = normalizeServiceName(row.serviceName || row.service);
    return `regular|${role}|${serviceName.toLowerCase()}|${row.shiftType || ""}|${row.duration || ""}`;
  }
  if (kind === "onetime") return `onetime|${normalizeServiceName(row.service).toLowerCase()}`;
  if (kind === "physio" || kind === "physio_assessment")
    return `physio|${normalizeServiceName(row.service).toLowerCase()}`;
  if (kind === "lab_test") {
    return `lab_test|${normalizeServiceName(row.department).toLowerCase()}|${normalizeServiceName(
      row.testGroup
    ).toLowerCase()}|${normalizeServiceName(row.parameterName).toLowerCase()}`;
  }
  if (kind === "lab_package") return `lab_package|${normalizeServiceName(row.packageName).toLowerCase()}`;

  return `other|${normalizeServiceName(row.service).toLowerCase()}`;
};

const identityOf = (row) => row?.identity || makeIdentity(row);

const normalizeRowFromLocal = (row) => {
  const baseFee = pickFee(row);
  const d = normalizeDiscount(row.discount);
  const out = { ...row, fee: baseFee, discount: d, finalFee: computeFinalFee(baseFee, d) };
  const identity = row?.identity || makeIdentity(out);
  return { ...out, identity };
};

const normalizeRowFromDB = (row) => {
  const raw = pickFee(row);
  const d = normalizeDiscount(row.discount);
  let baseFee = raw;
  if (ASSUME_DB_RETURNS_NET_FEE && d) baseFee = invertDiscount(raw, d);
  const out = { ...row, fee: baseFee, discount: d, finalFee: computeFinalFee(baseFee, d) };
  const identity = row?.identity || makeIdentity(out);
  return { ...out, identity };
};

const applyDraft = (savedList, draftList) => {
  const map = new Map(savedList.map((r) => [identityOf(r), r]));
  for (const d of draftList) {
    const id = identityOf(d);
    if (d?._change === "delete") map.delete(id);
    else map.set(id, d);
  }
  return Array.from(map.values()).filter((r) => r?._change !== "delete");
};

const encodeShiftDuration = (shiftType, duration) => {
  if (!shiftType) return undefined;
  return `${shiftType}|${duration || shiftType}`;
};

const decodeShiftDuration = (combined) => {
  if (!combined) return { shiftType: "", duration: "" };
  const opt = SHIFT_DURATION_OPTIONS.find((o) => o.value === combined);
  if (opt) return { shiftType: opt.shiftType, duration: opt.duration };
  const [shiftType, duration] = combined.split("|");
  return { shiftType: shiftType || "", duration: duration || shiftType || "" };
};

const normalizePrefillRole = (role) => {
  const token = String(role || "")
    .trim()
    .toLowerCase();
  if (
    token === "nursing-assistant" ||
    token === "nursing assistant" ||
    token === "nursingassistant"
  ) {
    return "nursing-assistant";
  }
  return "nurse";
};

const resolvePrefillShiftDuration = (prefill) => {
  const direct = String(prefill?.shiftDuration || "").trim();
  if (direct) {
    const { shiftType, duration } = decodeShiftDuration(direct);
    if (shiftType) return `${shiftType}|${duration || shiftType}`;
  }

  const shiftType = String(prefill?.shiftType || "").trim();
  const duration = String(prefill?.duration || "").trim();
  if (!shiftType) return "";
  return `${shiftType}|${duration || shiftType}`;
};

const findPrefillRegularFee = (rows, prefill) => {
  const list = Array.isArray(rows) ? rows : [];
  const targetShiftDuration = resolvePrefillShiftDuration(prefill);
  const targetRole = normalizePrefillRole(prefill?.role);
  const targetServices = uniqTrimmed(prefill?.services).map((item) =>
    String(item || "").trim().toLowerCase()
  );

  const targetServiceSet = new Set(targetServices);

  const match = list.find((row) => {
    if (String(row?.kind || "") !== "regular") return false;
    const rowShiftDuration = `${row?.shiftType || ""}|${row?.duration || row?.shiftType || ""}`;
    if (targetShiftDuration && rowShiftDuration !== targetShiftDuration) return false;
    if (targetRole && String(row?.role || "").trim().toLowerCase() !== targetRole) return false;

    const rowServices = uniqTrimmed(row?.services).map((item) =>
      String(item || "").trim().toLowerCase()
    );
    if (rowServices.length !== targetServices.length) return false;
    return rowServices.every((service) => targetServiceSet.has(service));
  });

  if (!match) return null;
  const fee = Number(match?.fee);
  return Number.isFinite(fee) ? fee : null;
};

const buildOneTimeRequestedDraftRows = (requestedServices = []) =>
  uniqTrimmed(requestedServices).map((service) =>
    normalizeRowFromLocal({
      key: uuid(),
      kind: "onetime",
      shiftType: "One Time",
      duration: "-",
      service,
      fee: 0,
      discount: null,
      notes: "",
      _change: "upsert",
    })
  );

/* ---------------- Lab Master normalizer ---------------- */
const pickFirst = (obj, keys) => {
  for (const k of keys) {
    const v = obj?.[k];
    if (v !== undefined && v !== null && String(v).trim() !== "") return v;
  }
  return "";
};

const normalizeLabMasterApi = (apiRows) => {
  const deptMap = new Map();

  const ensureDept = (department) => {
    const depKey = normalizeServiceName(department).toLowerCase();
    if (!depKey) return null;
    if (!deptMap.has(depKey)) {
      deptMap.set(depKey, { department: normalizeServiceName(department), testGroupsMap: new Map() });
    }
    return deptMap.get(depKey);
  };

  const ensureGroup = (deptNode, testGroup) => {
    const grpKey = normalizeServiceName(testGroup).toLowerCase();
    if (!grpKey) return null;
    if (!deptNode.testGroupsMap.has(grpKey)) {
      deptNode.testGroupsMap.set(grpKey, { testGroup: normalizeServiceName(testGroup), tests: [] });
    }
    return deptNode.testGroupsMap.get(grpKey);
  };

  const addTest = ({ department, testGroup, test }) => {
    const deptNode = ensureDept(department);
    if (!deptNode) return;

    const groupNode = ensureGroup(deptNode, testGroup || "General");
    if (!groupNode) return;

    const parameterName = normalizeServiceName(pickFirst(test, ["parameterName", "parameter", "testName", "name", "title"]));
    if (!parameterName) return;

    const sampleType = normalizeServiceName(
      pickFirst(test, ["sampleCategory", "sampleType", "specimenType", "specimentType", "specimen"])
    );
    const unit = normalizeServiceName(pickFirst(test, ["unit", "units", "uom"]));

    groupNode.tests.push({
      ...test,
      department: normalizeServiceName(department),
      testGroup: normalizeServiceName(testGroup || "General"),
      parameterName,
      sampleType,
      unit,
    });
  };

  const rows = safeArr(apiRows);

  for (const row of rows) {
    const department = normalizeServiceName(pickFirst(row, ["department", "dept", "departmentName", "name"]));

    const testGroups = safeArr(row?.testGroups);
    if (department && testGroups.length) {
      for (const g of testGroups) {
        const testGroup = normalizeServiceName(pickFirst(g, ["testGroup", "testGroupName", "groupName", "name", "title"]));
        const tests = safeArr(g?.tests);
        for (const t of tests) addTest({ department, testGroup, test: t });
      }
      continue;
    }

    const tg = normalizeServiceName(pickFirst(row, ["testGroup", "testGroupName", "groupName"]));
    const testsArr = safeArr(row?.tests);
    if (department && tg && testsArr.length) {
      for (const t of testsArr) addTest({ department, testGroup: tg, test: t });
      continue;
    }

    const parameterName = normalizeServiceName(pickFirst(row, ["parameterName", "parameter", "testName", "name", "title"]));
    if (department && parameterName) {
      addTest({ department, testGroup: tg || "General", test: row });
    }
  }

  const result = Array.from(deptMap.values()).map((dep) => ({
    department: dep.department,
    testGroups: Array.from(dep.testGroupsMap.values()).map((g) => ({ testGroup: g.testGroup, tests: g.tests })),
  }));

  result.sort((a, b) => a.department.localeCompare(b.department));
  for (const d of result) {
    d.testGroups.sort((a, b) => a.testGroup.localeCompare(b.testGroup));
    for (const g of d.testGroups) g.tests.sort((a, b) => a.parameterName.localeCompare(b.parameterName));
  }

  return result;
};

const matchNodeLoose = (items, getLabel, input) => {
  const v = normalizeServiceName(input).toLowerCase();
  if (!v) return null;
  let node = items.find((x) => normalizeServiceName(getLabel(x)).toLowerCase() === v);
  if (node) return node;
  node = items.find((x) => normalizeServiceName(getLabel(x)).toLowerCase().startsWith(v));
  if (node) return node;
  return items.find((x) => normalizeServiceName(getLabel(x)).toLowerCase().includes(v)) || null;
};

/* ---------------- Component ---------------- */
const Pricing = ({ pricingData, readOnly: readOnlyProp = undefined }) => {
  const navigate = useNavigate();
  const location = useLocation();

  const pricingSource = useMemo(() => resolvePricingSource(pricingData), [pricingData]);
  const isPricingDataPresent = pricingData !== undefined && pricingData !== null;
  const readOnly = readOnlyProp ?? isPricingDataPresent;

  const firstFromState = location.state?.first;
  const bookingPrefill = location.state?.prefill;
  const returnToPath = String(location.state?.returnTo || "").trim();
  const prefillVisitID = String(bookingPrefill?.visitID || "").trim();
  const lockNurseRegularPrefillFields =
    bookingPrefill?.source === "booking-nurse-regular-package" &&
    bookingPrefill?.lockFields === true;
  const lockPhysioAssessmentPrefillFields =
    bookingPrefill?.source === "booking-physio-assessment" &&
    bookingPrefill?.lockFields === true;
  const isNurseOneTimeNavigation =
    bookingPrefill?.source === "booking-nurse-onetime" &&
    bookingPrefill?.mode === "onetime";
  const oneTimeRequestedServices = uniqTrimmed(bookingPrefill?.requestedServices);
  const hasBookingPrefill =
    !!bookingPrefill &&
    (
      String(bookingPrefill?.visitID || "").trim().length > 0 ||
      Array.isArray(bookingPrefill?.services) ||
      String(bookingPrefill?.shiftDuration || "").trim().length > 0 ||
      String(bookingPrefill?.shiftType || "").trim().length > 0
    );

  const [onboardingFirst, setOnboardingFirst] = useState(() => {
    if (typeof firstFromState === "boolean") return firstFromState;
    return readOnboardingFirst();
  });

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const [serviceList, setServiceList] = useState([]);
  const [activeSection, setActiveSection] = useState("Nurse");
  const [nurseMode, setNurseMode] = useState("onetime");

  const [labMode, setLabMode] = useState("tests");
  const [labImportMode, setLabImportMode] = useState("single");

  const [savedNurseRegular, setSavedNurseRegular] = useState([]);
  const [savedNurseOneTime, setSavedNurseOneTime] = useState([]);
  const [savedPhysio, setSavedPhysio] = useState([]);
  const [savedDoctor, setSavedDoctor] = useState([]);

  const [savedLabTests, setSavedLabTests] = useState([]);
  const [savedLabPackages, setSavedLabPackages] = useState([]);

  const [draftNurseRegular, setDraftNurseRegular] = useState([]);
  const [draftNurseOneTime, setDraftNurseOneTime] = useState([]);
  const [draftPhysio, setDraftPhysio] = useState([]);
  const [draftDoctor, setDraftDoctor] = useState([]);

  const [draftLabTests, setDraftLabTests] = useState([]);
  const [draftLabPackages, setDraftLabPackages] = useState([]);

  const [nurseIndividualServices, setNurseIndividualServices] = useState([]);
  const [nurseGeneralServiceInfo, setNurseGeneralServiceInfo] = useState([]);
  const [physioServices, setPhysioServices] = useState([]);

  const [labMaster, setLabMaster] = useState([]);

  // ✅ list used for "Add Packages" -> Tests options (from getLabPackagesName)
  const [labPackageTestsMaster, setLabPackageTestsMaster] = useState([]);

  const [formShiftDuration, setFormShiftDuration] = useState("");
  const [formShiftType, setFormShiftType] = useState("");
  const [formDuration, setFormDuration] = useState("");
  const [formService, setFormService] = useState("");
  const [formFee, setFormFee] = useState(null);
  const [formServiceName, setFormServiceName] = useState("");
  const [formRole, setFormRole] = useState("");
  const [formServices, setFormServices] = useState([]);
  const [formDiscountType, setFormDiscountType] = useState("percentage");
  const [formDiscountValue, setFormDiscountValue] = useState(null);
  const [formNotes, setFormNotes] = useState("");

  const [formDepartment, setFormDepartment] = useState("");
  const [formTestGroup, setFormTestGroup] = useState("");
  const [formParameterName, setFormParameterName] = useState("");
  const [formSpecimentType, setFormSpecimentType] = useState("");
  const [formUnit, setFormUnit] = useState("mg/dl");
  const [formFastingRequired, setFormFastingRequired] = useState(true);

  // ✅ NEW: Prescription required (Lab Tests + Lab Packages)
  const [formPrescriptionRequired, setFormPrescriptionRequired] = useState(false);

  const [formPackageName, setFormPackageName] = useState("");
  const [formPackageTests, setFormPackageTests] = useState([]);

  const [editingKey, setEditingKey] = useState(null);
  const [editingIdentity, setEditingIdentity] = useState(null);

  const [bulkFileList, setBulkFileList] = useState([]);
  const [bulkParsedRows, setBulkParsedRows] = useState([]);
  const [bulkParseErrors, setBulkParseErrors] = useState([]);
  const prefillAppliedRef = useRef(false);
  const skipNextModeResetRef = useRef(false);
  const oneTimePrefillSeededRef = useRef(false);

  useEffect(() => {
    console.log("[Pricing] location.state:", location.state);
    console.log("[Pricing] bookingPrefill:", bookingPrefill);
    console.log("[Pricing] hasBookingPrefill:", hasBookingPrefill);
  }, [location.state, bookingPrefill, hasBookingPrefill]);

  useEffect(() => {
    const setVH = () => {
      const vh = window.innerHeight * 0.01;
      document.documentElement.style.setProperty("--app-vh", `${vh}px`);
    };
    setVH();
    window.addEventListener("resize", setVH);
    return () => window.removeEventListener("resize", setVH);
  }, []);

  const isNurse = activeSection === "Nurse";
  const isLab = activeSection === "Laboratory";
  const isSupportedSection = readOnly ? true : SUPPORTED_SECTIONS.has(activeSection);

  useEffect(() => {
    if (typeof firstFromState === "boolean") {
      setOnboardingFirst(firstFromState);
      secureLocalStorage.setItem(ONBOARDING_FIRST_KEY, firstFromState ? "1" : "0");
    }
  }, [firstFromState]);

  useEffect(() => {
    if (readOnly) return;
    if (isNurseOneTimeNavigation && !prefillAppliedRef.current) {
      // Wait for initial pricing fetch to finish because it clears draft rows.
      if (loading) return;
      if (activeSection !== "Nurse") {
        skipNextModeResetRef.current = true;
        setActiveSection("Nurse");
        return;
      }
      if (nurseMode !== "onetime") {
        skipNextModeResetRef.current = true;
        setNurseMode("onetime");
        return;
      }
      if (!oneTimePrefillSeededRef.current) {
        setDraftNurseOneTime((prev) =>
          prev.length > 0 ? prev : buildOneTimeRequestedDraftRows(oneTimeRequestedServices)
        );
        oneTimePrefillSeededRef.current = true;
      }
      prefillAppliedRef.current = true;
      return;
    }

    if (lockPhysioAssessmentPrefillFields && !prefillAppliedRef.current) {
      if (activeSection !== "Physiotherapist") {
        skipNextModeResetRef.current = true;
        setActiveSection("Physiotherapist");
        return;
      }

      skipNextModeResetRef.current = true;
      setFormService("Assessment");
      prefillAppliedRef.current = true;
      return;
    }

    if (!hasBookingPrefill) return;
    if (prefillAppliedRef.current) return;

    if (activeSection !== "Nurse") {
      skipNextModeResetRef.current = true;
      setActiveSection("Nurse");
      return;
    }

    if (nurseMode !== "regular") {
      skipNextModeResetRef.current = true;
      setNurseMode("regular");
      return;
    }

    const role = normalizePrefillRole(bookingPrefill?.role);
    const shiftDuration = resolvePrefillShiftDuration(bookingPrefill);
    const services = uniqTrimmed(bookingPrefill?.services);

    // Prevent the mode-change reset effect from wiping freshly applied prefill values.
    skipNextModeResetRef.current = true;
    setFormRole(role);
    setFormServices(services);

    if (shiftDuration) {
      setFormShiftDuration(shiftDuration);
      const { shiftType, duration } = decodeShiftDuration(shiftDuration);
      setFormShiftType(shiftType);
      setFormDuration(duration);
    } else {
      setFormShiftDuration("");
      setFormShiftType("");
      setFormDuration("");
    }

    prefillAppliedRef.current = true;
  }, [
    activeSection,
    nurseMode,
    bookingPrefill,
    hasBookingPrefill,
    isNurseOneTimeNavigation,
    oneTimeRequestedServices,
    lockPhysioAssessmentPrefillFields,
    loading,
    readOnly,
  ]);

  const resetForm = () => {
    setFormShiftDuration("");
    setFormShiftType("");
    setFormDuration("");
    setFormService("");
    setFormFee(null);
    setFormServiceName("");
    setFormRole("");
    setFormServices([]);
    setFormDiscountType("percentage");
    setFormDiscountValue(null);
    setFormNotes("");

    setFormDepartment("");
    setFormTestGroup("");
    setFormParameterName("");
    setFormSpecimentType("");
    setFormUnit("mg/dl");
    setFormFastingRequired(false);
    setFormPrescriptionRequired(false); // ✅ NEW

    setFormPackageName("");
    setFormPackageTests([]);
  };

  const cancelEdit = () => {
    setEditingKey(null);
    setEditingIdentity(null);
    resetForm();
  };

  const getServices = async () => {
    try {
      const res = await getLookupData();
      if (res?.error !== false || !Array.isArray(res?.data)) return;
      const rows = res.data.filter((x) => String(x?.type || "").trim() === "Services");

      const pickLabel = (x) => String(x?.label || x?.name || x?.value || "").trim();
      const uniqueOrdered = (arr) => {
        const out = [];
        const seen = new Set();
        for (const s of arr) {
          const v = String(s || "").trim();
          const key = v.toLowerCase();
          if (!v || seen.has(key)) continue;
          seen.add(key);
          out.push(v);
        }
        return out;
      };

      const nurseGeneral = uniqueOrdered(
        rows
          .filter((x) => String(x?.role || "").toLowerCase().trim() === "nurse")
          .filter((x) => String(x?.serviceType || "").toLowerCase().trim() === "general")
          .map(pickLabel)
          .filter(Boolean)
      );

      const nurseIndividual = uniqueOrdered(
        rows
          .filter((x) => String(x?.role || "").toLowerCase().trim() === "nurse")
          .map(pickLabel)
          .filter(Boolean)
      );

      const physio = uniqueOrdered([
        "Assessment",
        ...rows
          .filter((x) => {
            const role = String(x?.role || "").toLowerCase().trim();
            return role === "physiotherapist" || role === "physio";
          })
          .map(pickLabel)
          .filter(Boolean),
      ]);

      setNurseGeneralServiceInfo(nurseGeneral);
      setNurseIndividualServices(nurseIndividual);
      setPhysioServices(physio);
    } catch (e) {
      console.error("lookup error", e);
    }
  };

  const fetchLabMaster = async () => {
    try {
      const res = await getLabTests();
      if (res?.error !== false) {
        message.error(res?.message || "Failed to load lab master tests");
        setLabMaster([]);
        return;
      }
      const normalized = normalizeLabMasterApi(res?.data);
      setLabMaster(normalized);
    } catch (e) {
      console.error("getLabTests error", e);
      message.error("Failed to load lab master tests");
      setLabMaster([]);
    }
  };

  // getLabPackagesName -> only for packages "Tests" options
  const fetchLabPackage = async () => {
    try {
      const res = await getLabPackagesName();
      if (res?.error !== false) {
        message.error(res?.message || "Failed to load package tests list");
        setLabPackageTestsMaster([]);
        return;
      }
      const normalized = normalizeLabPackageTestsApi(res?.data);
      setLabPackageTestsMaster(normalized);
    } catch (e) {
      console.error("getLabPackagesName error", e);
      message.error("Failed to load package tests list");
      setLabPackageTestsMaster([]);
    }
  };

  const getRowTime = (r) => {
    const dt = r?.updatedAt || r?.createdAt;
    const t = dt ? new Date(dt).getTime() : 0;
    return Number.isFinite(t) ? t : 0;
  };

  const sortLatestFirst = (arr) => safeArr(arr).slice().sort((a, b) => getRowTime(b) - getRowTime(a));

  const normalizeDiscountType = (t) => {
    const s = String(t || "").trim().toLowerCase();
    if (s === "fixed" || s === "flat") return "fixed";
    if (s === "percentage" || s === "percent" || s === "%") return "percentage";
    return null;
  };

  const normalizeDiscountValue = (v) => {
    const n = Number(v);
    if (!Number.isFinite(n) || n <= 0) return null;
    return Math.trunc(n);
  };

  const labBulkIdentity = (r) =>
    `lab_test|${normalizeServiceName(r.department).toLowerCase()}|${normalizeServiceName(
      r.test_group
    ).toLowerCase()}|${normalizeServiceName(r.parameter_name).toLowerCase()}`;

  const upsertDraftRows = (setter, rows) => {
    setter((prev) => {
      const map = new Map(prev.map((r) => [identityOf(r), r]));
      for (const r of rows) map.set(identityOf(r), r);
      return Array.from(map.values());
    });
  };

  const hydrateFromPricingData = () => {
    const src = pricingSource;
    if (!src) {
      setSavedNurseRegular([]);
      setSavedNurseOneTime([]);
      setSavedPhysio([]);
      setSavedDoctor([]);
      setSavedLabTests([]);
      setSavedLabPackages([]);
      return;
    }

    const nurseNode = src?.nurseService || {};
    const regularSlots = safeArr(nurseNode?.regularShifts?.slots);
    const oneTimeServices = safeArr(nurseNode?.oneTimeShifts?.services);

    const regularTable = regularSlots
      .map((slot) => {
        const shiftType = shiftTypeFromShift(slot?.shift);
        const duration = String(slot?.type || "").trim();
        const fallbackName = `${duration || ""} Duty - ${shiftType}`.trim();
        const serviceName = normalizeServiceName(slot?.serviceName || slot?.name || slot?.service || fallbackName);
        const role = normalizeRole(slot?.role || slot?.staffRole || "Nurse");
        const services = uniqTrimmed(slot?.services);

        return normalizeRowFromDB({
          key: uuid(),
          kind: "regular",
          shiftType,
          duration: duration || "-",
          serviceName,
          role,
          services,
          fee: pickFee(slot),
          notes: slot?.notes || "",
          discount: slot?.discount || null,
          shift: slot?.shift || "",
          type: slot?.type || "",
          createdAt: slot?.createdAt,
          updatedAt: slot?.updatedAt,
        });
      })
      .filter((x) => x.shiftType && x.fee > 0);

    const oneTimeTable = oneTimeServices
      .map((srv) =>
        normalizeRowFromDB({
          key: uuid(),
          kind: "onetime",
          shiftType: "One Time",
          duration: "-",
          service: srv?.serviceType || srv?.name || srv?.label || "",
          fee: pickFee(srv),
          notes: srv?.notes || "",
          discount: srv?.discount || null,
          serviceType: srv?.serviceType || srv?.name || srv?.label || "",
          services: safeArr(srv?.services),
          createdAt: srv?.createdAt,
          updatedAt: srv?.updatedAt,
        })
      )
      .filter((x) => x.service && x.fee > 0);

    const physioNode = src?.physioService || src?.physioServices || src?.[PHYSIO_WRAPPER_KEY] || {};
    const assessmentFeesNum = toNumber(physioNode?.assessmentFees ?? physioNode?.assessmentFee ?? 0);

    const physioSrv = safeArr(physioNode?.services);
    const treatments = physioSrv
      .map((x) =>
        normalizeRowFromDB({
          key: uuid(),
          kind: "physio",
          shiftType: "Treatment",
          duration: "-",
          service: x?.name || x?.serviceType || x?.label || "",
          fee: pickFee(x),
          discount: x?.discount || null,
          notes: x?.notes || "",
          createdAt: x?.createdAt,
          updatedAt: x?.updatedAt,
        })
      )
      .filter((x) => x.service && x.fee > 0);

    const physioTable = [];
    if (assessmentFeesNum > 0) {
      physioTable.push(
        normalizeRowFromLocal({
          key: uuid(),
          kind: "physio_assessment",
          shiftType: "Visit",
          duration: "-",
          service: "Assessment",
          fee: assessmentFeesNum,
          discount: null,
          notes: "",
        })
      );
    }
    physioTable.push(...treatments);

    const labTestsRaw = extractLabTestsFromPricing(src);
    const labPackagesRaw = extractLabPackagesFromPricing(src);

    const labTestsTable = labTestsRaw
      .map((t) =>
        normalizeRowFromDB({
          key: uuid(),
          kind: "lab_test",
          department: t?.department || "",
          testGroup: t?.testGroup || "",
          parameterName: t?.parameterName || "",
          specimentType: t?.specimentType || t?.specimenType || "",
          unit: t?.unit || "",
          fastingRequired: normalizeFastingBool(t?.fastingHours),
          fastingHours: normalizeFastingBool(t?.fastingHours),

          // ✅ NEW
          prescriptionRequired: normalizeFastingBool(
            t?.prescriptionRequired ?? t?.prescription_required ?? t?.prescription
          ),

          fee: pickFee(t),
          notes: t?.notes || "",
          discount: t?.discount || null,
          createdAt: t?.createdAt,
          updatedAt: t?.updatedAt,
        })
      )
      .filter((x) => x.parameterName && x.fee > 0);

    const labPackagesTable = labPackagesRaw
      .map((p) =>
        normalizeRowFromDB({
          key: uuid(),
          kind: "lab_package",
          packageName: p?.packageName || p?.name || "",
          tests: uniqTrimmed(p?.testsIncluded || p?.tests || p?.services || []),
          fastingRequired: normalizeFastingBool(p?.fastingRequired),

          // ✅ NEW
          prescriptionRequired: normalizeFastingBool(
            p?.prescriptionRequired ?? p?.prescription_required ?? p?.prescription
          ),

          fee: pickFee(p),
          notes: p?.notes || "",
          discount: p?.discount || null,
          createdAt: p?.createdAt,
          updatedAt: p?.updatedAt,
        })
      )
      .filter((x) => x.packageName && x.fee > 0);

    setSavedNurseRegular(sortLatestFirst(regularTable));
    setSavedNurseOneTime(sortLatestFirst(oneTimeTable));
    setSavedPhysio(sortLatestFirst(physioTable));
    setSavedDoctor([]);

    setSavedLabTests(sortLatestFirst(labTestsTable));
    setSavedLabPackages(sortLatestFirst(labPackagesTable));

    setDraftNurseRegular([]);
    setDraftNurseOneTime([]);
    setDraftPhysio([]);
    setDraftDoctor([]);
    setDraftLabTests([]);
    setDraftLabPackages([]);
  };

  const fetchPartnerData = async () => {
    try {
      const res = await getPartner();
      if (res?.error !== false) {
        message.error(res?.message || "Failed to load pricing data");
        return;
      }
      const data = res?.data || {};
      setServiceList(extractPartnerServices(data));

      const nurseNode = data?.pricingInfo?.nurseService || {};
      const regularShifts = safeArr(nurseNode?.regularShifts?.slots);
      const oneTimeShifts = safeArr(nurseNode?.oneTimeShifts?.services);

      const regularTableData = regularShifts
        .map((slot) => {
          const shiftType = shiftTypeFromShift(slot?.shift);
          const duration = String(slot?.type || "").trim();
          const fallbackName = `${duration || ""} Duty - ${shiftType}`.trim();
          const serviceName = normalizeServiceName(slot?.serviceName || slot?.name || slot?.service || fallbackName);
          const role = normalizeRole(slot?.role || slot?.staffRole || "Nurse");
          const services = uniqTrimmed(slot?.services);

          return normalizeRowFromDB({
            key: uuid(),
            kind: "regular",
            shiftType,
            duration,
            serviceName,
            role,
            services,
            fee: pickFee(slot),
            notes: slot?.notes || "",
            discount: slot?.discount || null,
            createdAt: slot?.createdAt,
            updatedAt: slot?.updatedAt,
          });
        })
        .filter((row) => row.shiftType && row.fee > 0);

      const oneTimeTableData = oneTimeShifts
        .map((srv) =>
          normalizeRowFromDB({
            key: uuid(),
            kind: "onetime",
            shiftType: "One Time",
            duration: "-",
            service: srv?.serviceType || srv?.name || srv?.label || "",
            fee: pickFee(srv),
            notes: srv?.notes || "",
            discount: srv?.discount || null,
            services: safeArr(srv?.services),
            createdAt: srv?.createdAt,
            updatedAt: srv?.updatedAt,
          })
        )
        .filter((row) => row.service && row.fee > 0);

      setSavedNurseRegular(sortLatestFirst(regularTableData));
      setSavedNurseOneTime(sortLatestFirst(oneTimeTableData));

      const physioNode =
        data?.pricingInfo?.physioService ||
        data?.pricingInfo?.physioServices ||
        data?.pricingInfo?.[PHYSIO_WRAPPER_KEY] ||
        {};

      const assessmentFeesNum = toNumber(physioNode?.assessmentFees ?? physioNode?.assessmentFee ?? 0);

      const physioSrv = safeArr(physioNode?.services);
      const treatments = physioSrv
        .map((x) =>
          normalizeRowFromDB({
            key: uuid(),
            kind: "physio",
            shiftType: "Treatment",
            duration: "-",
            service: x?.name || x?.serviceType || x?.label || "",
            fee: pickFee(x),
            discount: x?.discount || null,
            notes: x?.notes || "",
            createdAt: x?.createdAt,
            updatedAt: x?.updatedAt,
          })
        )
        .filter((x) => x.service && x.fee > 0);

      const physioTable = [];
      if (assessmentFeesNum > 0) {
        physioTable.push(
          normalizeRowFromLocal({
            key: uuid(),
            kind: "physio_assessment",
            shiftType: "Visit",
            duration: "-",
            service: "Assessment",
            fee: assessmentFeesNum,
            discount: null,
            notes: "",
          })
        );
      }
      physioTable.push(...treatments);
      setSavedPhysio(sortLatestFirst(physioTable));
      setSavedDoctor([]);

      const pricingInfo = data?.pricingInfo || {};
      const labTestsRaw = extractLabTestsFromPricing(pricingInfo);
      const labPackagesRaw = extractLabPackagesFromPricing(pricingInfo);

      const labTestsTable = labTestsRaw
        .map((t) =>
          normalizeRowFromDB({
            key: uuid(),
            kind: "lab_test",
            department: t?.department || "",
            testGroup: t?.testGroup || "",
            parameterName: t?.parameterName || "",
            specimentType: t?.specimentType || t?.specimenType || "",
            unit: t?.unit || "",
            fastingRequired: normalizeFastingBool(t?.fastingHours),
            fastingHours: normalizeFastingBool(t?.fastingHours),

            // ✅ NEW
            prescriptionRequired: normalizeFastingBool(
              t?.prescriptionRequired ?? t?.prescription_required ?? t?.prescription
            ),

            fee: pickFee(t),
            notes: t?.notes || "",
            discount: t?.discount || null,
            createdAt: t?.createdAt,
            updatedAt: t?.updatedAt,
          })
        )
        .filter((x) => x.parameterName && x.fee > 0);

      const labPackagesTable = labPackagesRaw
        .map((p) =>
          normalizeRowFromDB({
            key: uuid(),
            kind: "lab_package",
            packageName: p?.packageName || p?.name || "",
            tests: uniqTrimmed(p?.testsIncluded || p?.tests || p?.services || []),
            fastingRequired: normalizeFastingBool(p?.fastingRequired),

            // ✅ NEW
            prescriptionRequired: normalizeFastingBool(
              p?.prescriptionRequired ?? p?.prescription_required ?? p?.prescription
            ),

            fee: pickFee(p),
            notes: p?.notes || "",
            discount: p?.discount || null,
            createdAt: p?.createdAt,
            updatedAt: p?.updatedAt,
          })
        )
        .filter((x) => x.packageName && x.fee > 0);

      setSavedLabTests(sortLatestFirst(labTestsTable));
      setSavedLabPackages(sortLatestFirst(labPackagesTable));

      setDraftNurseRegular([]);
      setDraftNurseOneTime([]);
      setDraftPhysio([]);
      setDraftDoctor([]);
      setDraftLabTests([]);
      setDraftLabPackages([]);
    } catch (e) {
      console.error("fetchPartnerData error", e);
      message.error("Failed to load pricing data");
    }
  };

  useEffect(() => {
    let mounted = true;
    const run = async () => {
      setLoading(true);
      try {
        if (readOnly) {
          hydrateFromPricingData();
        } else {
          await Promise.all([fetchPartnerData(), getServices(), fetchLabMaster(), fetchLabPackage()]);
        }
      } finally {
        if (mounted) setLoading(false);
      }
    };
    run();
    return () => {
      mounted = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [readOnly, pricingSource]);

  useEffect(() => {
    if (activeSection !== "Nurse") return;
    if (isNurseOneTimeNavigation) return;
    if (nurseMode === "onetime" && !savedNurseOneTime.length && savedNurseRegular.length) {
      setNurseMode("regular");
    } else if (nurseMode === "regular" && !savedNurseRegular.length && savedNurseOneTime.length) {
      setNurseMode("onetime");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeSection, isNurseOneTimeNavigation, savedNurseRegular.length, savedNurseOneTime.length]);

  useEffect(() => {
    if (skipNextModeResetRef.current) {
      skipNextModeResetRef.current = false;
      return;
    }
    cancelEdit();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeSection, nurseMode, labMode]);

  const serviceTabs = useMemo(() => {
    if (readOnly) return buildTabsFromPricing(pricingSource);
    if (!Array.isArray(serviceList) || serviceList.length === 0) return ALL_SERVICE_TABS;

    const set = new Set(serviceList.map((s) => String(s || "").toLowerCase().trim()));
    const hasNurse = set.has("nurse") || set.has("nursing assistant") || set.has("nursingassistant");
    const hasPhysio = set.has("physiotherapist") || set.has("physio") || set.has("physiotherapy");
    // const hasLab = set.has("laboratory") || set.has("lab") || set.has("diagnostics") || set.has("diagnostic");

    const tabs = [];
    if (hasNurse) tabs.push({ label: "Nurse", value: "Nurse" });
    if (hasPhysio) tabs.push({ label: "Physiotherapist", value: "Physiotherapist" });
    // if (hasLab) tabs.push({ label: "Laboratory", value: "Laboratory" });
    return tabs.length ? tabs : ALL_SERVICE_TABS;
  }, [readOnly, pricingSource, serviceList]);

  useEffect(() => {
    if (serviceTabs.length === 1) {
      setActiveSection(serviceTabs[0].value);
      return;
    }
    if (!serviceTabs.some((t) => t.value === activeSection)) {
      setActiveSection(serviceTabs[0]?.value || "Nurse");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [serviceTabs]);

  const savedActiveData = useMemo(() => {
    if (activeSection === "Nurse") return nurseMode === "regular" ? savedNurseRegular : savedNurseOneTime;
    if (activeSection === "Physiotherapist") return savedPhysio;
    if (activeSection === "Laboratory") return labMode === "tests" ? savedLabTests : savedLabPackages;
    return savedDoctor;
  }, [
    activeSection,
    nurseMode,
    labMode,
    savedNurseRegular,
    savedNurseOneTime,
    savedPhysio,
    savedDoctor,
    savedLabTests,
    savedLabPackages,
  ]);

  const draftActiveData = useMemo(() => {
    if (activeSection === "Nurse") return nurseMode === "regular" ? draftNurseRegular : draftNurseOneTime;
    if (activeSection === "Physiotherapist") return draftPhysio;
    if (activeSection === "Laboratory") return labMode === "tests" ? draftLabTests : draftLabPackages;
    return draftDoctor;
  }, [
    activeSection,
    nurseMode,
    labMode,
    draftNurseRegular,
    draftNurseOneTime,
    draftPhysio,
    draftDoctor,
    draftLabTests,
    draftLabPackages,
  ]);

  const hasDraftChanges = draftActiveData.length > 0;

  const departmentOptions = useMemo(() => {
    const deps = uniqTrimmed(labMaster.map((d) => d.department));
    return deps.map((x) => ({ value: x, label: x }));
  }, [labMaster]);

  const selectedDeptNode = useMemo(() => {
    return matchNodeLoose(labMaster, (d) => d.department, formDepartment);
  }, [labMaster, formDepartment]);

  const testGroupOptions = useMemo(() => {
    const groups = safeArr(selectedDeptNode?.testGroups).map((g) => g.testGroup);
    const uniq = uniqTrimmed(groups);
    return uniq.map((x) => ({ value: x, label: x }));
  }, [selectedDeptNode]);

  const selectedGroupNode = useMemo(() => {
    const groups = safeArr(selectedDeptNode?.testGroups);
    return matchNodeLoose(groups, (g) => g.testGroup, formTestGroup);
  }, [selectedDeptNode, formTestGroup]);

  const parameterOptions = useMemo(() => {
    const params = safeArr(selectedGroupNode?.tests).map((t) => t.parameterName);
    const uniq = uniqTrimmed(params);
    return uniq.map((x) => ({ value: x, label: x }));
  }, [selectedGroupNode]);

  const findMasterTest = (department, testGroup, parameterName) => {
    const dep = normalizeServiceName(department).toLowerCase();
    const grp = normalizeServiceName(testGroup).toLowerCase();
    const par = normalizeServiceName(parameterName).toLowerCase();

    const depNode = labMaster.find((d) => normalizeServiceName(d.department).toLowerCase() === dep);
    if (!depNode) return null;

    const grpNode = safeArr(depNode.testGroups).find((g) => normalizeServiceName(g.testGroup).toLowerCase() === grp);
    if (!grpNode) return null;

    return safeArr(grpNode.tests).find((t) => normalizeServiceName(t.parameterName).toLowerCase() === par) || null;
  };

  useEffect(() => {
    if (!isLab || labMode !== "tests") return;
    const dep = normalizeServiceName(formDepartment);
    const grp = normalizeServiceName(formTestGroup);
    const par = normalizeServiceName(formParameterName);
    if (!dep || !grp || !par) return;

    const t = findMasterTest(dep, grp, par);
    if (!t) return;

    const specimen = t.sampleType || t.sampleCategory;
    const unit = t.unit;

    if (specimen) setFormSpecimentType(specimen);
    if (unit) setFormUnit(unit);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLab, labMode, formDepartment, formTestGroup, formParameterName]);

  const servicesListContent = (list, title = "Services") => {
    const arr = uniqTrimmed(list);
    if (!arr.length) return <div className="text-xs text-gray-500">No services</div>;
    return (
      <div style={{ maxWidth: 320 }}>
        <div className="text-xs font-semibold text-gray-800 mb-2">{title}</div>
        <ul className="list-disc pl-4 text-xs text-gray-700 space-y-1">
          {arr.map((x) => (
            <li key={x}>{x}</li>
          ))}
        </ul>
      </div>
    );
  };

  const generalInfoContent = useMemo(() => {
    const list = safeArr(nurseGeneralServiceInfo);
    if (!list.length) return <div className="text-xs text-gray-500">No general service details found.</div>;
    return (
      <div style={{ maxWidth: 320 }}>
        <div className="text-xs font-semibold text-gray-800 mb-2">Included in General Services</div>
        <ul className="list-disc pl-4 text-xs text-gray-700 space-y-1">
          {list.map((x) => (
            <li key={x}>{x}</li>
          ))}
        </ul>
      </div>
    );
  }, [nurseGeneralServiceInfo]);

  const renderDiscountField = () => {
    const fee = Number(formFee || 0);
    const maxPercent = 99;
    const maxFixed = fee > 0 ? Math.max(0, Math.trunc(fee) - 1) : MAX_FEE;

    return (
      <div className="w-full">
        <label className={labelCls}>Discount</label>
        <div className="flex items-center gap-2 min-w-0">
          <InputNumber
            placeholder="0"
            value={formDiscountValue}
            onChange={(v) => {
              if (formDiscountType === "percentage") setFormDiscountValue(clampInt(v, { min: 0, max: maxPercent }));
              else setFormDiscountValue(clampInt(v, { min: 0, max: maxFixed }));
            }}
            min={0}
            max={formDiscountType === "percentage" ? maxPercent : maxFixed}
            precision={0}
            step={1}
            controls={false}
            size="small"
            className="flex-1 min-w-0 p-2"
            style={{ borderRadius: 8, height: 38 }}
            parser={(val) => String(val ?? "").replace(/[^\d]/g, "")}
            formatter={(val) => String(val ?? "").replace(/[^\d]/g, "")}
          />
          <Select
            value={formDiscountType}
            onChange={(t) => {
              setFormDiscountType(t);
              setFormDiscountValue(null);
            }}
            size="small"
            style={{ width: 70, borderRadius: 8, height: 38 }}
            dropdownMatchSelectWidth={false}
            showSearch={false}
          >
            <Select.Option value="percentage">%</Select.Option>
            <Select.Option value="fixed">₹</Select.Option>
          </Select>
        </div>
      </div>
    );
  };

  const renderServiceLabelWithInfo = () => {
    const showInfo =
      isNurse && nurseMode === "onetime" && isGeneralService(formService) && nurseGeneralServiceInfo.length > 0;
    return (
      <div className="flex items-center gap-1.5">
        <span className="text-xs font-semibold text-gray-600 leading-tight">Service</span>
        {showInfo && (
          <Popover content={generalInfoContent} trigger="click" placement="right">
            <button
              type="button"
              className="flex items-center justify-center w-5 h-5 rounded-full border border-gray-200 bg-white text-gray-500 hover:bg-gray-50 font-poppins"
            >
              <Info className="w-3 h-3" />
            </button>
          </Popover>
        )}
      </div>
    );
  };

  const renderServiceInput = () => {
    if (readOnly) return null;

    if (activeSection === "Nurse" && nurseMode === "onetime") {
      const oneTimeOptions = [
        ...nurseIndividualServices.filter((s) => !isExcludedOneTimeService(s)).map((s) => ({ value: s, label: s })),
      ];
      const disableServiceWhenEditing = Boolean(editingKey);

      return (
        <AutoComplete
          className="w-full mb-2 font-poppins"
          options={oneTimeOptions}
          value={formService || ""}
          onChange={setFormService}
          placeholder="Select or type service"
          disabled={disableServiceWhenEditing}
          filterOption={(input, option) =>
            (option?.label ?? option?.value ?? "").toLowerCase().includes(input.toLowerCase())
          }
        >
          <Input size="small" disabled={disableServiceWhenEditing} style={{ borderRadius: 8, height: 38 }} />
        </AutoComplete>
      );
    }

    if (activeSection === "Physiotherapist") {
      const options = physioServices.map((s) => ({ value: s, label: s }));
      const disableServiceWhenEditing =
        Boolean(editingKey) || lockPhysioAssessmentPrefillFields;
      return (
        <AutoComplete
          className="w-full"
          options={options}
          value={formService || ""}
          onChange={setFormService}
          placeholder="Select or type service"
          disabled={disableServiceWhenEditing}
          filterOption={(input, option) =>
            (option?.label ?? option?.value ?? "").toLowerCase().includes(input.toLowerCase())
          }
        >
          <Input size="small" disabled={disableServiceWhenEditing} style={{ borderRadius: 8, height: 38 }} />
        </AutoComplete>
      );
    }

    return null;
  };

  const renderActionButtons = () => (
    <div className="flex items-center justify-center gap-2">
      {editingKey && (
        <button
          onClick={cancelEdit}
          className="w-10 h-10 flex items-center justify-center rounded-full border border-gray-200 bg-white text-gray-500 hover:bg-gray-50"
          type="button"
        >
          <Ban className="w-4 h-4" />
        </button>
      )}
      <button
        onClick={handleAddOrUpdate}
        className="w-10 h-10 flex items-center justify-center rounded-full bg-blue-600 text-white hover:bg-blue-700 shadow-sm"
        type="button"
      >
        {editingKey ? <Check className="w-4 h-4" /> : <Plus className="w-4 h-4" />}
      </button>
    </div>
  );

  const upsertDraftRow = (setter, row) => {
    setter((prev) => {
      const id = identityOf(row);
      const filtered = prev.filter((r) => identityOf(r) !== id);
      return [...filtered, row];
    });
  };

  const startEditRow = (record) => {
    if (readOnly || !isSupportedSection) return;
    if (record?._change === "delete") return;

    setEditingKey(record.key);
    setEditingIdentity(identityOf(record));

    if (activeSection === "Nurse") setNurseMode(record.kind === "regular" ? "regular" : "onetime");
    if (activeSection === "Laboratory") setLabMode(record.kind === "lab_package" ? "packages" : "tests");

    if (record.kind === "regular") {
      setFormServiceName(record.serviceName || "");
      setFormRole(normalizeRole(record.role || "Nurse") || "Nurse");
      setFormServices(uniqTrimmed(record.services));
      const combined = encodeShiftDuration(record.shiftType, record.duration);
      setFormShiftDuration(combined || "");
      setFormShiftType(record.shiftType || "");
      setFormDuration(record.duration || "");
    } else if (record.kind === "onetime" || record.kind === "physio" || record.kind === "physio_assessment") {
      setFormService(record.service || "");
    } else if (record.kind === "lab_test") {
      setFormDepartment(record.department || "");
      setFormTestGroup(record.testGroup || "");
      setFormParameterName(record.parameterName || "");
      setFormSpecimentType(record.specimentType || "Blood");
      setFormUnit(record.unit || "mg/dl");
      setFormFastingRequired(Boolean(record.fastingRequired));
      setFormPrescriptionRequired(Boolean(record.prescriptionRequired)); // ✅ NEW
    } else if (record.kind === "lab_package") {
      setFormPackageName(record.packageName || "");
      setFormPackageTests(uniqTrimmed(record.tests));
      setFormFastingRequired(typeof record.fastingRequired === "boolean" ? record.fastingRequired : false);
      setFormPrescriptionRequired(Boolean(record.prescriptionRequired)); // ✅ NEW
    }

    setFormFee(clampInt(record.fee, { min: 0, max: MAX_FEE }));
    setFormNotes(record.notes || "");

    const d = normalizeDiscount(record.discount);
    setFormDiscountType(d?.type || "percentage");
    setFormDiscountValue(d?.value != null ? clampInt(d.value, { min: 0, max: MAX_FEE }) : null);
  };

  const queueDeleteSaved = (record) => {
    if (readOnly || !isSupportedSection) return;
    const del = normalizeRowFromLocal({ ...record, key: uuid(), identity: identityOf(record), _change: "delete" });

    if (activeSection === "Nurse") {
      if (record.kind === "regular") upsertDraftRow(setDraftNurseRegular, del);
      else upsertDraftRow(setDraftNurseOneTime, del);
      return;
    }
    if (activeSection === "Physiotherapist") {
      upsertDraftRow(setDraftPhysio, del);
      return;
    }
    if (activeSection === "Laboratory") {
      if (record.kind === "lab_package") upsertDraftRow(setDraftLabPackages, del);
      else upsertDraftRow(setDraftLabTests, del);
    }
  };

  const removeDraftRow = (record) => {
    if (readOnly || !isSupportedSection) return;
    if (editingKey === record.key) cancelEdit();

    if (activeSection === "Nurse") {
      if (record.kind === "regular") setDraftNurseRegular((p) => p.filter((x) => x.key !== record.key));
      else setDraftNurseOneTime((p) => p.filter((x) => x.key !== record.key));
      return;
    }
    if (activeSection === "Physiotherapist") {
      setDraftPhysio((p) => p.filter((x) => x.key !== record.key));
      return;
    }
    if (activeSection === "Laboratory") {
      if (record.kind === "lab_package") setDraftLabPackages((p) => p.filter((x) => x.key !== record.key));
      else setDraftLabTests((p) => p.filter((x) => x.key !== record.key));
    }
  };

  const validateDiscount = (baseFee) => {
    const fee = Number(baseFee || 0);
    const hasDisc = formDiscountValue != null && formDiscountValue !== "" && Number(formDiscountValue) > 0;
    if (!hasDisc) return true;

    const v = Number(formDiscountValue);
    if (formDiscountType === "percentage") {
      if (!Number.isFinite(v) || v <= 0) return true;
      if (!Number.isInteger(v)) {
        message.warning("Percentage discount must be a whole number (no decimals)");
        return false;
      }
      if (v >= 100) {
        message.warning("Percentage discount must be below 100");
        return false;
      }
      return true;
    }

    if (!Number.isFinite(v) || v <= 0) return true;
    if (!Number.isInteger(v)) {
      message.warning("Fixed discount must be a whole number (no decimals)");
      return false;
    }
    if (fee > 0 && v >= fee) {
      message.warning("Fixed discount must be less than Fee (not equal / not greater)");
      return false;
    }
    return true;
  };

  const getDiscountObjFromForm = () => {
    const v = Number(formDiscountValue);
    if (!Number.isFinite(v) || v <= 0) return null;
    if (formDiscountType === "percentage") return { type: "percentage", value: Math.trunc(v) };
    return { type: "fixed", value: Math.trunc(v) };
  };

  const checkLabTestDuplicate = ({ department, testGroup, parameterName }) => {
    const dep = normalizeServiceName(department).toLowerCase();
    const grp = normalizeServiceName(testGroup).toLowerCase();
    const par = normalizeServiceName(parameterName).toLowerCase();

    const isDup = (item) =>
      normalizeServiceName(item.department).toLowerCase() === dep &&
      normalizeServiceName(item.testGroup).toLowerCase() === grp &&
      normalizeServiceName(item.parameterName).toLowerCase() === par;

    if (savedLabTests.some(isDup)) return { valid: false, msg: "This test already exists in Saved Pricing." };

    const editedId = editingKey;
    if (draftLabTests.some((item) => item.key !== editedId && isDup(item)))
      return { valid: false, msg: "You already have a pending change for this test." };

    return { valid: true };
  };

  const checkLabPackageDuplicate = (packageName) => {
    const name = normalizeServiceName(packageName).toLowerCase();
    const isDup = (p) => normalizeServiceName(p.packageName).toLowerCase() === name;

    if (savedLabPackages.some(isDup)) return { valid: false, msg: "This package already exists in Saved Pricing." };

    const editedId = editingKey;
    if (draftLabPackages.some((p) => p.key !== editedId && isDup(p)))
      return { valid: false, msg: "You already have a pending change for this package." };

    return { valid: true };
  };

  const handleAddOrUpdate = () => {
    if (readOnly) return;
    if (!isSupportedSection) return message.info(`${activeSection} pricing is coming soon`);

    const feeInt = clampInt(formFee, { min: 0, max: MAX_FEE });
    if (feeInt == null || feeInt <= 0) return message.warning("Please enter valid Fee (max 5 digits)");
    if (!validateDiscount(feeInt)) return;

    const discountObj = getDiscountObjFromForm();
    const isEditing = Boolean(editingKey);

    if (activeSection === "Nurse") {
      if (nurseMode === "regular") {
        const serviceName = normalizeServiceName(formServiceName);
        const role = normalizeRole(formRole);

        if (!serviceName) return message.warning("Please enter Service Name");
        if (!role) return message.warning("Please select Role");

        if (!formShiftDuration) return message.warning("Please select Shift & Duration");
        const { shiftType: resolvedShift, duration: resolvedDuration } = decodeShiftDuration(formShiftDuration);
        if (!resolvedShift) return message.warning("Please select Shift & Duration");

        const servicesFinal = uniqTrimmed(formServices);
        if (!servicesFinal.length) return message.warning("Please select/enter at least one Service");

        if (!isEditing) {
          const dupCheck = checkRegularDuplicate({
            serviceName,
            role,
            shiftType: resolvedShift,
            duration: resolvedDuration,
          });
          if (!dupCheck.valid) return message.warning(dupCheck.msg);
        }

        const row = normalizeRowFromLocal({
          key: isEditing ? editingKey : uuid(),
          kind: "regular",
          serviceName,
          role,
          services: servicesFinal,
          shiftType: resolvedShift,
          duration: resolvedDuration,
          fee: feeInt,
          discount: discountObj,
          notes: formNotes,
          shift: shiftFromShiftType(resolvedShift),
          type: resolvedDuration,
          identity: isEditing ? editingIdentity : undefined,
          _change: "upsert",
        });

        upsertDraftRow(setDraftNurseRegular, row);
        cancelEdit();
        return;
      }

      if (!normalizeServiceName(formService)) return message.warning("Please select or type Service");
      const serviceName = normalizeServiceName(formService);

      if (!isEditing) {
        const oneTimeDup = checkOneTimeDuplicate(serviceName);
        if (!oneTimeDup.valid) return message.warning(oneTimeDup.msg);
      }

      const row = normalizeRowFromLocal({
        key: isEditing ? editingKey : uuid(),
        kind: "onetime",
        shiftType: "One Time",
        duration: "-",
        service: serviceName,
        fee: feeInt,
        discount: discountObj,
        notes: formNotes,
        serviceType: serviceName,
        identity: isEditing ? editingIdentity : undefined,
        _change: "upsert",
      });

      upsertDraftRow(setDraftNurseOneTime, row);
      cancelEdit();
      return;
    }

    if (activeSection === "Physiotherapist") {
      if (!normalizeServiceName(formService)) return message.warning("Please select or type Service");
      const serviceName = normalizeServiceName(formService);
      const isAssessment = isSameService(serviceName, "Assessment");

      const row = normalizeRowFromLocal({
        key: isEditing ? editingKey : uuid(),
        kind: isAssessment ? "physio_assessment" : "physio",
        shiftType: isAssessment ? "Visit" : "Treatment",
        duration: "-",
        service: serviceName,
        fee: feeInt,
        discount: discountObj,
        notes: formNotes,
        identity: isEditing ? editingIdentity : undefined,
        _change: "upsert",
      });

      upsertDraftRow(setDraftPhysio, row);
      cancelEdit();
      return;
    }

    if (activeSection === "Laboratory") {
      if (labMode === "tests") {
        const department = normalizeServiceName(formDepartment);
        const testGroup = normalizeServiceName(formTestGroup);
        const parameterName = normalizeServiceName(formParameterName);
        const specimentType = normalizeServiceName(formSpecimentType);
        const unit = normalizeServiceName(formUnit);

        if (!department) return message.warning("Please select/enter Department");
        if (!testGroup) return message.warning("Please select/enter Test Group");
        if (!parameterName) return message.warning("Please select/enter Parameter Name");
        if (!specimentType) return message.warning("Please enter Specimen Type");
        if (!unit) return message.warning("Please enter Unit");

        const dup = checkLabTestDuplicate({ department, testGroup, parameterName });
        if (!dup.valid) return message.warning(dup.msg);

        const row = normalizeRowFromLocal({
          key: isEditing ? editingKey : uuid(),
          kind: "lab_test",
          department,
          testGroup,
          parameterName,
          specimentType,
          unit,
          fastingRequired: Boolean(formFastingRequired),
          fastingHours: Boolean(formFastingRequired),

          // ✅ NEW
          prescriptionRequired: Boolean(formPrescriptionRequired),

          fee: feeInt,
          discount: discountObj,
          notes: formNotes,
          identity: isEditing ? editingIdentity : undefined,
          _change: "upsert",
        });

        upsertDraftRow(setDraftLabTests, row);
        cancelEdit();
        return;
      }

      const packageName = normalizeServiceName(formPackageName);
      const tests = uniqTrimmed(formPackageTests);

      if (!packageName) return message.warning("Please enter Package Name");
      if (!tests.length) return message.warning("Please select/enter at least one Test");

      const dup = checkLabPackageDuplicate(packageName);
      if (!dup.valid) return message.warning(dup.msg);

      const row = normalizeRowFromLocal({
        key: isEditing ? editingKey : uuid(),
        kind: "lab_package",
        packageName,
        tests,
        fastingRequired: Boolean(formFastingRequired),

        // ✅ NEW
        prescriptionRequired: Boolean(formPrescriptionRequired),

        fee: feeInt,
        discount: discountObj,
        notes: formNotes,
        identity: isEditing ? editingIdentity : undefined,
        _change: "upsert",
      });

      upsertDraftRow(setDraftLabPackages, row);
      cancelEdit();
      return;
    }
  };

  const checkOneTimeDuplicate = (newService) => {
    const targetNorm = normalizeServiceName(newService).toLowerCase();
    const savedExist = savedNurseOneTime.some((item) => normalizeServiceName(item.service).toLowerCase() === targetNorm);
    if (savedExist) return { valid: false, msg: "This One-Time Service already exists in Saved Pricing." };

    const editedId = editingKey;
    const draftExist = draftNurseOneTime.some(
      (item) => item.key !== editedId && normalizeServiceName(item.service).toLowerCase() === targetNorm
    );
    if (draftExist) return { valid: false, msg: "You already have a pending change for this One-Time Service." };

    return { valid: true };
  };

  const checkRegularDuplicate = (newRow) => {
    const targetNorm = normalizeServiceName(newRow.serviceName).toLowerCase();
    const targetRole = normalizeRole(newRow.role).toLowerCase();
    const targetShift = newRow.shiftType.toLowerCase();
    const targetDuration = newRow.duration.toLowerCase();

    const isDuplicate = (item) => {
      const iName = normalizeServiceName(item.serviceName).toLowerCase();
      const iRole = normalizeRole(item.role).toLowerCase();
      const iShift = item.shiftType.toLowerCase();
      const iDuration = item.duration.toLowerCase();
      return iName === targetNorm && iRole === targetRole && iShift === targetShift && iDuration === targetDuration;
    };

    const savedExist = savedNurseRegular.some(isDuplicate);
    if (savedExist) return { valid: false, msg: "Shift combination (Service, Role, Shift, Duration) already exists." };

    const editedId = editingKey;
    const draftExist = draftNurseRegular.some((item) => item.key !== editedId && isDuplicate(item));
    if (draftExist) return { valid: false, msg: "You already have a pending change for this Shift combination." };

    return { valid: true };
  };

  const roleToAPI = (v) => (normalizeRole(v) || "Nurse").trim().toLowerCase();

  const handleSaveSection = async () => {
    if (readOnly) return;
    if (!isSupportedSection) return message.info(`${activeSection} pricing is coming soon`);
    if (!draftActiveData.length) return;

    setSaving(true);
    try {
      if (activeSection === "Nurse") {
        if (nurseMode === "regular") {
          const merged = applyDraft(savedNurseRegular, draftNurseRegular);
          const regular = merged.map((item) => ({
            shift: shiftFromShiftType(item.shiftType),
            type: item.duration,
            serviceName: normalizeServiceName(item.serviceName || ""),
            role: roleToAPI(item.role),
            services: uniqTrimmed(item.services),
            fee: clampInt(item.fee, { min: 0, max: MAX_FEE }) || 0,
            notes: item.notes || "",
            discount: item.discount ? { type: item.discount.type, value: Number(item.discount.value) } : undefined,
          }));

          const payload = {
            dutyType: "regularShifts",
            nurseService: { regularShifts: { slots: regular } },
            ...(prefillVisitID ? { visitID: prefillVisitID } : {}),
            ...(prefillVisitID
              ? { feePerDayForVisit: findPrefillRegularFee(merged, bookingPrefill) }
              : {}),
          };
          const resp = await servicePricing(payload);

          if (resp?.error === false) {
            message.success("Saved successfully!");
            secureLocalStorage.setItem("pricingDone", true);
            await fetchPartnerData();
            if (bookingPrefill) {
              navigate(returnToPath || "/partner/booking", {
                replace: true,
                state: {
                  fromPricingPrefill: true,
                  prefillSource: bookingPrefill?.source || "",
                },
              });
            }
          } else message.error(resp?.message || "Failed to save");
          return;
        }

        const merged = applyDraft(savedNurseOneTime, draftNurseOneTime);
        if (isNurseOneTimeNavigation) {
          const missingFee = merged.some(
            (item) => !Number.isFinite(Number(item?.fee)) || Number(item?.fee) <= 0
          );
          if (missingFee) {
            return message.warning("Click the edit icon and enter pricing for each requested service.");
          }
        }
        const oneTime = merged.map((item) => {
          const obj = {
            serviceType: item.service,
            fee: clampInt(item.fee, { min: 0, max: MAX_FEE }) || 0,
            notes: item.notes || "",
            discount: item.discount ? { type: item.discount.type, value: Number(item.discount.value) } : undefined,
          };
          if (isGeneralService(item.service)) obj.services = safeArr(nurseGeneralServiceInfo);
          return obj;
        });

        const payload = {
          dutyType: "oneTimeShifts",
          nurseService: { oneTimeShifts: { services: oneTime } },
          ...(prefillVisitID ? { visitID: prefillVisitID } : {}),
        };
        const resp = await servicePricing(payload);

        if (resp?.error === false) {
          message.success("Saved successfully!");
          secureLocalStorage.setItem("pricingDone", true);
          await fetchPartnerData();
          if (bookingPrefill) {
            navigate(returnToPath || "/partner/booking", {
              replace: true,
              state: {
                fromPricingPrefill: true,
                prefillSource: bookingPrefill?.source || "",
              },
            });
          }
        } else message.error(resp?.message || "Failed to save");
        return;
      }

      if (activeSection === "Physiotherapist") {
        const merged = applyDraft(savedPhysio, draftPhysio);
        const assessmentRow = merged.find((x) => isSameService(x.service, "Assessment"));
        const assessmentFees = assessmentRow ? clampInt(assessmentRow.fee, { min: 0, max: MAX_FEE }) || 0 : 0;

        const services = merged
          .filter((x) => !isSameService(x.service, "Assessment"))
          .map((item) => ({
            name: item.service,
            fee: clampInt(item.fee, { min: 0, max: MAX_FEE }) || 0,
            notes: item.notes || "",
            discount: item.discount ? { type: item.discount.type, value: Number(item.discount.value) } : undefined,
          }))
          .filter((x) => x.name && x.fee > 0);

        const payload = {
          dutyType: "physioService",
          [PHYSIO_WRAPPER_KEY]: { assessmentFees, services },
          ...(prefillVisitID ? { visitID: prefillVisitID } : {}),
        };
        const resp = await servicePricing(payload);

        if (resp?.error === false) {
          message.success("Saved successfully!");
          secureLocalStorage.setItem("pricingDone", true);
          await fetchPartnerData();
        } else message.error(resp?.message || "Failed to save");
        return;
      }

      if (activeSection === "Laboratory") {
        if (labMode === "tests") {
          const merged = applyDraft(savedLabTests, draftLabTests);

          const tests = merged
            .map((t) => ({
              department: normalizeServiceName(t.department),
              testGroup: normalizeServiceName(t.testGroup),
              parameterName: normalizeServiceName(t.parameterName),
              specimentType: normalizeServiceName(t.specimentType),
              unit: normalizeServiceName(t.unit),
              fastingRequired: Boolean(t.fastingRequired),

              // ✅ NEW
              prescriptionRequired: Boolean(t.prescriptionRequired),

              fee: clampInt(t.fee, { min: 0, max: MAX_FEE }) || 0,
              notes: t.notes || "",
              discount: t.discount ? { type: t.discount.type, value: Number(t.discount.value) } : undefined,
            }))
            .filter((t) => t.parameterName && t.fee > 0);

          // matches GET shape pricingInfo.labService.tests
          const payload = { dutyType: "labService", type: "tests", labService: tests };
          const resp = await servicePricing(payload);

          if (resp?.error === false) {
            message.success("Saved successfully!");
            secureLocalStorage.setItem("pricingDone", true);
            await fetchPartnerData();
          } else {
            message.error(resp?.message || "Failed to save");
          }
          return;
        }

        const merged = applyDraft(savedLabPackages, draftLabPackages);
        const packages = merged
          .map((p) => ({
            name: normalizeServiceName(p.packageName),
            testsIncluded: uniqTrimmed(p.tests),
            fee: clampInt(p.fee, { min: 0, max: MAX_FEE }) || 0,
            notes: p.notes || "",
            fastingRequired: Boolean(p.fastingRequired),

            // ✅ NEW
            prescriptionRequired: Boolean(p.prescriptionRequired),

            discount: p.discount ? { type: p.discount.type, value: Number(p.discount.value) } : undefined,
          }))
          .filter((p) => p.name && p.fee > 0);

        const payload = { dutyType: "labService", labServices: packages, type: "packages" };

        const resp = await servicePricing(payload);

        if (resp?.error === false) {
          message.success("Saved successfully!");
          secureLocalStorage.setItem("pricingDone", true);
          await fetchPartnerData();
        } else {
          message.error(resp?.message || "Failed to save");
        }
        return;
      }
    } catch (e) {
      console.error(e);
      message.error("Failed to save");
    } finally {
      setSaving(false);
    }
  };

  const savedIdentitySet = useMemo(() => new Set(savedActiveData.map(identityOf)), [savedActiveData]);

  const baseCols = useMemo(() => {
    if (activeSection === "Laboratory") {
      if (labMode === "tests") {
        return [
          { title: "Department", dataIndex: "department", key: "department", width: 140, ellipsis: true },
          { title: "Test Group", dataIndex: "testGroup", key: "testGroup", width: 140, ellipsis: true },
          { title: "Parameter Name", dataIndex: "parameterName", key: "parameterName", width: 170, ellipsis: true },
          { title: "S. Type", dataIndex: "specimentType", key: "specimentType", width: 110, ellipsis: true },
          { title: "Unit", dataIndex: "unit", key: "unit", width: 85, ellipsis: true },
          {
            title: "Fee",
            dataIndex: "finalFee",
            key: "finalFee",
            width: 90,
            render: (_, record) => {
              if (record?._change === "delete") return <span className="text-gray-400">--</span>;
              const hasDiscount = record?.discount && Number(record.discount.value) > 0;
              return (
                <div className="leading-tight">
                  <div className="font-medium text-gray-900">₹{record.finalFee ?? record.fee}</div>
                  {hasDiscount && <div className="text-[10px] text-gray-400 line-through">₹{record.fee}</div>}
                </div>
              );
            },
          },
          {
            title: "Discount",
            dataIndex: "discount",
            key: "discount",
            width: 85,
            render: (d, record) => (record?._change === "delete" ? "--" : formatDiscount(normalizeDiscount(d))),
          },
          {
            title: "Fasting",
            dataIndex: "fastingRequired",
            key: "fastingRequired",
            width: 80,
            render: (v, record) => (record?._change === "delete" ? "--" : v ? "Yes" : "No"),
          },

          // ✅ NEW
          {
            title: "Prescription",
            dataIndex: "prescriptionRequired",
            key: "prescriptionRequired",
            width: 110,
            render: (v, record) => (record?._change === "delete" ? "--" : v ? "Yes" : "No"),
          },

          {
            title: "Notes",
            dataIndex: "notes",
            key: "notes",
            width: 180,
            ellipsis: true,
            render: (v) => (v ? <span title={v}>{v}</span> : "--"),
          },
        ];
      }

      return [
        {
          title: "Package Name",
          dataIndex: "packageName",
          key: "packageName",
          width: 180,
          ellipsis: true,
          render: (v) => v || "--",
        },
        {
          title: "Tests",
          dataIndex: "tests",
          key: "tests",
          width: 280,
          ellipsis: true,
          render: (arr = []) => {
            const list = uniqTrimmed(arr);
            const txt = list.length ? list.join(", ") : "--";
            return (
              <div className="flex items-center gap-2 min-w-0">
                <span className="truncate" title={txt}>
                  {txt}
                </span>
                {list.length > 0 && (
                  <Popover content={servicesListContent(list, "Tests")} trigger="click" placement="right">
                    <button
                      type="button"
                      className="flex-none flex items-center justify-center w-5 h-5 rounded-full border border-gray-200 bg-white text-gray-500 hover:bg-gray-50"
                      title="View tests"
                    >
                      <Info className="w-3 h-3" />
                    </button>
                  </Popover>
                )}
              </div>
            );
          },
        },
        {
          title: "Fee",
          dataIndex: "finalFee",
          key: "finalFee",
          width: 100,
          render: (_, record) => {
            if (record?._change === "delete") return <span className="text-gray-400">--</span>;
            const hasDiscount = record?.discount && Number(record.discount.value) > 0;
            return (
              <div className="leading-tight">
                <div className="font-medium text-gray-900">₹{record.finalFee ?? record.fee}</div>
                {hasDiscount && <div className="text-[10px] text-gray-400 line-through">₹{record.fee}</div>}
              </div>
            );
          },
        },
        {
          title: "Fasting",
          dataIndex: "fastingRequired",
          key: "fastingRequired",
          width: 85,
          render: (v, record) => {
            if (record?._change === "delete") return "--";
            if (typeof v !== "boolean") return "--";
            return v ? "Yes" : "No";
          },
        },

        // ✅ NEW
        {
          title: "Prescription",
          dataIndex: "prescriptionRequired",
          key: "prescriptionRequired",
          width: 110,
          render: (v, record) => {
            if (record?._change === "delete") return "--";
            if (typeof v !== "boolean") return "--";
            return v ? "Yes" : "No";
          },
        },

        {
          title: "Discount",
          dataIndex: "discount",
          key: "discount",
          width: 85,
          render: (d, record) => (record?._change === "delete" ? "--" : formatDiscount(normalizeDiscount(d))),
        },
        {
          title: "Notes",
          dataIndex: "notes",
          key: "notes",
          width: 180,
          ellipsis: true,
          render: (v) => (v ? <span title={v}>{v}</span> : "--"),
        },
      ];
    }

    const cols = [
      {
        title: "Shift",
        dataIndex: "shiftType",
        key: "shiftType",
        width: 105,
        ellipsis: true,
        render: (v, record) => {
          if (record?.kind === "regular" && record?.duration && record.duration !== "-") {
            const opt = SHIFT_DURATION_OPTIONS.find((o) => o.shiftType === v && o.duration === record.duration);
            return opt ? opt.label : `${v} ${record.duration}`;
          }
          return v || "--";
        },
      },
    ];

    if (!(activeSection === "Nurse" && nurseMode === "regular")) {
      cols.push({ title: "Duration", dataIndex: "duration", key: "duration", width: 80, ellipsis: true });
    }

    if (activeSection === "Nurse" && nurseMode === "regular") {
      cols.push(
        { title: "Service Name", dataIndex: "serviceName", key: "serviceName", width: 150, ellipsis: true },
        {
          title: "Role",
          dataIndex: "role",
          key: "role",
          width: 110,
          ellipsis: true,
          render: (v) => capitalizeFirstLetter(normalizeRole(v)),
        },
        {
          title: "Services",
          dataIndex: "services",
          key: "services",
          width: 220,
          ellipsis: true,
          render: (arr) => {
            const list = uniqTrimmed(arr);
            const txt = list.length ? list.join(", ") : "--";
            return (
              <div className="flex items-center gap-2 min-w-0">
                <span className="truncate" title={txt}>
                  {txt}
                </span>
                {list.length > 0 && (
                  <Popover content={servicesListContent(list, "Services Details")} trigger="click" placement="right">
                    <button
                      type="button"
                      className="flex-none flex items-center justify-center w-5 h-5 rounded-full border border-gray-200 bg-white text-gray-500 hover:bg-gray-50"
                      title="View services"
                    >
                      <Info className="w-3 h-3" />
                    </button>
                  </Popover>
                )}
              </div>
            );
          },
        }
      );
    } else {
      cols.push({
        title: "Service",
        dataIndex: "service",
        key: "service",
        width: 220,
        ellipsis: true,
        render: (v, record) => {
          const rowServices = uniqTrimmed(record?.services);
          const showInfoBtn =
            rowServices.length > 0 ||
            (activeSection === "Nurse" &&
              nurseMode === "onetime" &&
              isGeneralService(record?.service) &&
              nurseGeneralServiceInfo.length > 0);

          const content = rowServices.length > 0 ? servicesListContent(rowServices, "Service Details") : generalInfoContent;

          return (
            <div className="flex items-center gap-2 min-w-0">
              <span className="truncate" title={v}>
                {v || "--"}
              </span>
              {showInfoBtn && (
                <Popover content={content} trigger="click" placement="right">
                  <button
                    type="button"
                    className="flex-none flex items-center justify-center w-5 h-5 rounded-full border border-gray-200 bg-white text-gray-500 hover:bg-gray-50"
                    title="View details"
                  >
                    <Info className="w-3 h-3" />
                  </button>
                </Popover>
              )}
            </div>
          );
        },
      });
    }

    cols.push(
      {
        title: "Fee",
        dataIndex: "finalFee",
        key: "finalFee",
        width: 105,
        render: (_, record) => {
          if (record?._change === "delete") return <span className="text-gray-400">--</span>;
          const hasDiscount = record?.discount && Number(record.discount.value) > 0;
          return (
            <div className="leading-tight">
              <div className="font-medium text-gray-900">₹{record.finalFee ?? record.fee}</div>
              {hasDiscount && <div className="text-[10px] text-gray-400 line-through">₹{record.fee}</div>}
            </div>
          );
        },
      },
      {
        title: "Discount",
        dataIndex: "discount",
        key: "discount",
        width: 85,
        render: (d, record) => (record?._change === "delete" ? "--" : formatDiscount(normalizeDiscount(d))),
      },
      {
        title: "Notes",
        dataIndex: "notes",
        key: "notes",
        width: 140,
        ellipsis: true,
        render: (v) => (v ? <span title={v}>{v}</span> : "--"),
      }
    );

    return cols;
  }, [activeSection, nurseMode, labMode, nurseGeneralServiceInfo.length, generalInfoContent]);

  const savedColumns = useMemo(() => {
    if (readOnly) return baseCols;
    return [
      ...baseCols,
      {
        title: "Action",
        key: "action",
        width: 80,
        fixed: "right",
        render: (_, record) => (
          <div className="flex items-center gap-2">
            <button
              type="button"
              className="text-blue-500 hover:text-blue-700 disabled:opacity-50"
              onClick={() => startEditRow(record)}
              disabled={saving}
            >
              <Pencil className="w-4 h-4" />
            </button>
            <button
              type="button"
              className="text-red-500 hover:text-red-700 disabled:opacity-50"
              onClick={() => queueDeleteSaved(record)}
              disabled={saving}
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        ),
      },
    ];
  }, [readOnly, baseCols, saving]);

  const draftColumns = useMemo(() => {
    if (readOnly) return baseCols;
    return [
      {
        title: "Status",
        key: "status",
        width: 90,
        render: (_, record) => {
          if (record?._change === "delete") return <span className="text-red-600 font-medium text-xs">Remove</span>;
          const existsInSaved = savedIdentitySet.has(identityOf(record));
          return (
            <span className={`font-medium text-xs ${existsInSaved ? "text-amber-600" : "text-green-600"}`}>
              {existsInSaved ? "Update" : "New"}
            </span>
          );
        },
      },
      ...baseCols,
      {
        title: "Act",
        key: "action",
        width: 80,
        fixed: "right",
        render: (_, record) => (
          <div className="flex items-center gap-2">
            <button
              type="button"
              className="text-blue-500 hover:text-blue-700 disabled:opacity-50"
              onClick={() => startEditRow(record)}
              disabled={saving || record?._change === "delete"}
              hidden={saving || record?._change === "delete"}
            >
              <Pencil className="w-4 h-4" />
            </button>
            <button
              type="button"
              className="text-red-500 hover:text-red-700 disabled:opacity-50"
              onClick={() => removeDraftRow(record)}
              disabled={saving}
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        ),
      },
    ];
  }, [readOnly, baseCols, saving, savedIdentitySet]);

  const readFileAsArrayBuffer = (file) =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (e) => resolve(e.target.result);
      reader.onerror = reject;
      reader.readAsArrayBuffer(file);
    });

  const normKey = (h) => String(h || "").trim().toLowerCase().replace(/[^a-z0-9]/g, "");

  const normalizeRowKeys = (obj) => {
    const out = {};
    for (const [k, v] of Object.entries(obj || {})) out[normKey(k)] = v;
    return out;
  };

  const pickFirstVal = (row, keys) => {
    for (const k of keys) {
      const v = row?.[k];
      if (v !== undefined && v !== null && String(v).trim() !== "") return v;
    }
    return "";
  };

  const parseFee = (v) => {
    const raw = String(v ?? "").replace(/[^\d.]/g, "");
    const n = Number(raw);
    if (!Number.isFinite(n)) return null;
    return clampInt(n, { min: 0, max: MAX_FEE });
  };

  const parseDiscountFromRow = (r) => {
    const discountCell = pickFirstVal(r, ["discount", "disc"]);
    if (discountCell) return normalizeDiscount(String(discountCell));

    const typeCell = String(pickFirstVal(r, ["discounttype", "disctype", "dtype"]) || "").trim().toLowerCase();
    const valueCell = pickFirstVal(r, ["discountvalue", "discvalue", "dvalue"]);
    if (valueCell === "" || valueCell === null || valueCell === undefined) return null;

    const valueNum = Number(String(valueCell).replace(/[^\d.]/g, ""));
    if (!Number.isFinite(valueNum) || valueNum <= 0) return null;

    const type =
      typeCell.includes("%") || typeCell.includes("percent") || typeCell === "percentage"
        ? "percentage"
        : typeCell.includes("₹") || typeCell.includes("rs") || typeCell.includes("fixed")
          ? "fixed"
          : "percentage";

    return normalizeDiscount({ type, value: Math.trunc(valueNum) });
  };

  const parseLabTestsFile = async (file) => {
    const buf = await readFileAsArrayBuffer(file);
    const wb = XLSX.read(buf, { type: "array" });
    const ws = wb.Sheets[wb.SheetNames[0]];
    const json = XLSX.utils.sheet_to_json(ws, { defval: "" });

    const rows = [];
    const errors = [];
    const seenInFile = new Set();

    for (let i = 0; i < json.length; i++) {
      const raw = normalizeRowKeys(json[i]);
      const rowNum = i + 2;

      const department = normalizeServiceName(pickFirstVal(raw, ["department"]));
      const test_group = normalizeServiceName(pickFirstVal(raw, ["testgroup", "test_group"]));
      const parameter_name = normalizeServiceName(
        pickFirstVal(raw, ["parametername", "parameter_name", "testname", "name"])
      );
      const specimen_type = normalizeServiceName(
        pickFirstVal(raw, [
          "specimentype",
          "specimenttype",
          "specimen",
          "sampletype",
          "stype",
        ])
      );
      const unit = normalizeServiceName(pickFirstVal(raw, ["unit", "uom", "units"]));
      const fee = parseFee(pickFirstVal(raw, ["fee", "fees", "price", "amount"]));
      const fasting_required = normalizeFastingBool(pickFirstVal(raw, ["fastingrequired", "fasting_required", "fasting"]));

      // ✅ NEW
      const prescription_required = normalizeFastingBool(
        pickFirstVal(raw, [
          "prescriptionrequired",
          "prescription_required",
          "prescription",
          "rxrequired",
          "rx_required",
        ])
      );

      const discount_type = String(pickFirstVal(raw, ["discounttype", "discount_type"]) || "").trim().toLowerCase();
      const discount_value_raw = pickFirstVal(raw, ["discountvalue", "discount_value"]);

      const discount_value =
        discount_value_raw !== ""
          ? Math.trunc(Number(String(discount_value_raw).replace(/[^\d.]/g, "")))
          : null;

      const notes = String(pickFirstVal(raw, ["notes"]) || "").trim();

      // validations
      if (!department) { errors.push(`Row ${rowNum}: Department is required`); continue; }
      if (!test_group) { errors.push(`Row ${rowNum}: Test Group is required`); continue; }
      if (!parameter_name) { errors.push(`Row ${rowNum}: Parameter Name is required`); continue; }
      if (!specimen_type) { errors.push(`Row ${rowNum}: Specimen Type is required`); continue; }
      if (!unit) { errors.push(`Row ${rowNum}: Unit is required`); continue; }
      if (!fee || fee <= 0) { errors.push(`Row ${rowNum}: Fee must be > 0`); continue; }

      if (discount_value != null) {
        if (!discount_type) {
          errors.push(`Row ${rowNum}: Discount Type must be "percentage" or "fixed"`);
          continue;
        }
        if (discount_type === "percentage" && (discount_value <= 0 || discount_value >= 100)) {
          errors.push(`Row ${rowNum}: Percentage discount must be 1 to 99`);
          continue;
        }
        if (discount_type === "fixed" && discount_value >= fee) {
          errors.push(`Row ${rowNum}: Fixed discount must be less than Fee`);
          continue;
        }
      }

      // de-dupe inside file
      const identity = `lab_test|${department.toLowerCase()}|${test_group.toLowerCase()}|${parameter_name.toLowerCase()}`;
      if (seenInFile.has(identity)) {
        errors.push(`Row ${rowNum}: Duplicate test in file (${department} / ${test_group} / ${parameter_name})`);
        continue;
      }
      seenInFile.add(identity);

      rows.push({
        department,
        test_group,
        parameter_name,
        specimen_type,
        unit,
        fee,
        fasting_required: Boolean(fasting_required),

        // ✅ NEW
        prescription_required: Boolean(prescription_required),

        discount_type: discount_type || null,
        discount_value: Number.isFinite(discount_value) ? discount_value : null,
        notes,
      });
    }

    return { rows, errors };
  };

  const downloadLabTestsSample = () => {
    const header = [
      "department",
      "test_group",
      "parameter_name",
      "specimen_type",
      "unit",
      "fee",
      "fasting_required",

      // ✅ NEW
      "prescription_required",

      "discount_type",
      "discount_value",
      "notes",
    ];

    const rows = [
      {
        department: "Biochemistry",
        test_group: "Electrolytes",
        parameter_name: "Chloride (Cl-)",
        specimen_type: "Blood",
        unit: "mmol/L",
        fee: 250,
        fasting_required: "TRUE",

        // ✅ NEW
        prescription_required: "FALSE",

        discount_type: "percentage",
        discount_value: 10,
        notes: "Sample note",
      },
      {
        department: "Endocrinology",
        test_group: "Hormones",
        parameter_name: "Insulin - Fasting",
        specimen_type: "Blood",
        unit: "uIU/mL",
        fee: 900,
        fasting_required: "TRUE",

        // ✅ NEW
        prescription_required: "TRUE",

        discount_type: "fixed",
        discount_value: 50,
        notes: "",
      },
    ];

    const aoa = [header, ...rows.map((r) => header.map((h) => r[h] ?? ""))];
    const ws = XLSX.utils.aoa_to_sheet(aoa);

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "LabTests");
    XLSX.writeFile(wb, "lab-tests-bulk-sample.xlsx");
  };

  const regularServicesOptions = useMemo(() => {
    const all = uniqTrimmed([...safeArr(nurseGeneralServiceInfo), ...safeArr(nurseIndividualServices)]);
    return all.map((s) => ({ label: s, value: s }));
  }, [nurseGeneralServiceInfo, nurseIndividualServices]);

  const labAllParameterNames = useMemo(() => {
    const all = [];
    for (const dep of labMaster) {
      for (const grp of safeArr(dep.testGroups)) {
        for (const t of safeArr(grp.tests)) all.push(t.parameterName);
      }
    }
    return uniqTrimmed([...all, ...savedLabTests.map((x) => x.parameterName)]);
  }, [labMaster, savedLabTests]);

  const labTestOptionsForPackages = useMemo(() => {
    const merged = uniqTrimmed([...labPackageTestsMaster]);
    return merged.map((x) => ({ label: x, value: x }));
  }, [labAllParameterNames, labPackageTestsMaster]);

  const tableScrollX = useMemo(() => {
    if (activeSection === "Nurse" && nurseMode === "regular") return 1100;
    if (activeSection === "Laboratory" && labMode === "tests") return 1320; // ✅ updated
    if (activeSection === "Laboratory" && labMode === "packages") return 1100; // ✅ updated
    return 950;
  }, [activeSection, nurseMode, labMode]);

  const bulkUploadProps = {
    multiple: false,
    accept: ".xlsx,.xls,.csv",
    fileList: bulkFileList,
    showUploadList: true,
    beforeUpload: async (file) => {
      try {
        const { rows, errors } = await parseLabTestsFile(file);
        setBulkFileList([file]);
        setBulkParsedRows(rows);
        setBulkParseErrors(errors);
        message.success(`Parsed ${rows.length} valid rows${errors.length ? `, ${errors.length} errors` : ""}`);
      } catch (e) {
        console.error(e);
        message.error("Failed to read file. Please upload a valid Excel/CSV.");
        setBulkFileList([]);
        setBulkParsedRows([]);
        setBulkParseErrors([]);
      }
      return false;
    },
    onRemove: () => {
      setBulkFileList([]);
      setBulkParsedRows([]);
      setBulkParseErrors([]);
    },
  };

  const handleBulkLabUpload = async () => {
    if (readOnly) return;
    if (!bulkParsedRows.length) return message.warning("No valid rows to upload");

    const savedSet = new Set(
      savedLabTests.map(
        (t) =>
          `lab_test|${normalizeServiceName(t.department).toLowerCase()}|${normalizeServiceName(
            t.testGroup
          ).toLowerCase()}|${normalizeServiceName(t.parameterName).toLowerCase()}`
      )
    );

    const uploadRows = [];
    const skipped = [];

    for (const r of bulkParsedRows) {
      const id = labBulkIdentity(r);
      if (savedSet.has(id)) {
        skipped.push(`${r.department} / ${r.test_group} / ${r.parameter_name} already in Saved Pricing`);
        continue;
      }

      uploadRows.push({
        department: normalizeServiceName(r.department),
        test_group: normalizeServiceName(r.test_group),
        parameter_name: normalizeServiceName(r.parameter_name),
        specimen_type: normalizeServiceName(r.specimen_type),
        unit: normalizeServiceName(r.unit),
        fee: clampInt(r.fee, { min: 0, max: MAX_FEE }) || 0,
        fasting_required: Boolean(r.fasting_required),

        // ✅ NEW
        prescription_required: Boolean(r.prescription_required),

        discount_type: normalizeDiscountType(r.discount_type),
        discount_value: normalizeDiscountValue(r.discount_value),
        notes: String(r.notes || ""),
      });
    }

    if (!uploadRows.length) {
      if (skipped.length) message.info(`Skipped ${skipped.length} rows (already saved)`);
      return message.warning("Nothing to upload");
    }

    const payload = { labTests: uploadRows };

    setSaving(true);
    try {
      const resp = await addLabBulk(payload);

      if (resp?.error === false) {
        message.success(`Uploaded ${uploadRows.length} lab test(s) successfully`);
        if (skipped.length) message.warning(`Skipped ${skipped.length} rows (already saved)`);

        await fetchPartnerData();

        setBulkFileList([]);
        setBulkParsedRows([]);
        setBulkParseErrors([]);
      } else {
        message.error(resp?.message || "Bulk upload failed");
      }
    } catch (e) {
      console.error(e);
      message.error("Bulk upload failed");
    } finally {
      setSaving(false);
    }
  };

  const importMenu = {
    items: [
      { key: "single", label: "Enter Single Tests", onClick: () => setLabImportMode("single") },
      { key: "bulk", label: "Import Bulk Tests", onClick: () => setLabImportMode("bulk") },
    ],
  };

  if (loading) return <Loader loading={loading} />;

  return (

    <div
      className="pricing-shell w-full max-w-full bg-[#F4F6FB] px-3 py-3 overflow-hidden"
      style={{
        height: "calc(var(--app-vh, 1vh) * 92)",
        fontFamily: "'Poppins', sans-serif",
        boxSizing: "border-box",
      }}
    >
      <style>{`
      @supports (height: 100dvh) { .pricing-shell { height: 100dvh; } }
      @supports (height: 100svh) { .pricing-shell { height: 100svh; } }

      .pricing-shell, .pricing-shell * { box-sizing: border-box; }

      .pricing-shell .ant-input,
      .pricing-shell .ant-input-number,
      .pricing-shell .ant-select-selector {
        border-radius: 8px !important;
        height: 38px !important;
        max-width: 100% !important;
      }

      .pricing-shell .ant-input-number-input,
      .pricing-shell .ant-select-selection-item,
      .pricing-shell .ant-select-selection-placeholder,
      .pricing-shell .ant-select-selection-search-input,
      .pricing-shell .ant-select-item,
      .pricing-shell .ant-select-item-option-content {
        font-family: "Poppins", sans-serif !important;
      }

      .pricing-shell .ant-table { border-radius: 12px !important; }

      /* ✅ MAIN FIX: stop Ant Table scroll.x from increasing the whole page width */
      .pricing-shell .ant-table-wrapper { width: 100% !important; max-width: 100% !important; }
      .pricing-shell .ant-table { max-width: 100% !important; }
      .pricing-shell .ant-table-container { max-width: 100% !important; overflow-x: auto !important; }
      .pricing-shell .ant-table-content { max-width: 100% !important; overflow-x: auto !important; }

      /* ✅ prevent long select/tags from affecting layout */
      .pricing-shell .ant-select { max-width: 100% !important; }

      .pricing-scroll {
        -webkit-overflow-scrolling: touch;
        overscroll-behavior: contain;
      }
    `}</style>

      <div className="bg-white h-full rounded-xl shadow-sm border border-[#E1E7FF] flex flex-col overflow-hidden max-w-full">
        {/* ✅ keep the page from horizontal scrolling; internal table wrappers scroll instead */}
        <div className="pricing-scroll flex-1 min-h-0 overflow-y-auto overflow-x-hidden w-full max-w-full">
          <div className="flex items-center justify-center mb-1 pt-4">
            <div className="w-full max-w-[520px] min-w-0 px-4">
              {serviceTabs.length > 1 ? (
                <ToggleTabs options={serviceTabs} active={activeSection} setActive={setActiveSection} />
              ) : (
                <div className="text-sm font-semibold text-gray-800">{serviceTabs[0]?.label || activeSection}</div>
              )}
            </div>
          </div>

          {isNurse && (
            <div className="flex mb-4 px-4 min-w-0">
              <div className="relative w-full max-w-[350px] min-w-0">
                <div className="flex border-b border-gray-200 min-w-0">
                  <button
                    type="button"
                    className={`flex-1 text-center pb-3 pt-1 font-medium transition-colors ${nurseMode === "onetime" ? "text-blue-600" : "text-gray-500 hover:text-blue-500"
                      }`}
                    onClick={() => {
                      setNurseMode("onetime");
                      if (!readOnly) cancelEdit();
                    }}
                  >
                    One-Time Service
                  </button>
                  <button
                    type="button"
                    className={`flex-1 text-center pb-3 pt-1 font-medium transition-colors ${nurseMode === "regular" ? "text-blue-600" : "text-gray-500 hover:text-blue-500"
                      }`}
                    onClick={() => {
                      setNurseMode("regular");
                      if (!readOnly) cancelEdit();
                    }}
                  >
                    Regular Service
                  </button>
                </div>

                <div className="absolute bottom-0 left-0 flex w-full">
                  <span
                    className="block h-[2.5px] rounded bg-blue-600 transition-all duration-300"
                    style={{
                      position: "absolute",
                      width: "50%",
                      transition: "left 0.3s cubic-bezier(.4,0,.2,1)",
                      left: nurseMode === "onetime" ? "0%" : "50%",
                    }}
                  />
                </div>
              </div>
            </div>
          )}


          {activeSection === "Laboratory" && (
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-4 px-4 gap-3 min-w-0">
              <div className="relative w-full sm:max-w-[350px] min-w-0">
                <div className="flex border-b border-gray-200 min-w-0">
                  <button
                    type="button"
                    className={`flex-1 text-center pb-3 pt-1 font-medium transition-colors ${labMode === "tests" ? "text-blue-600" : "text-gray-500 hover:text-blue-500"
                      }`}
                    onClick={() => {
                      setLabMode("tests");
                      if (!readOnly) cancelEdit();
                    }}
                  >
                    {readOnly ? "Tests" : "Add Tests"}
                  </button>

                  <button
                    type="button"
                    className={`flex-1 text-center pb-3 pt-1 font-medium transition-colors ${labMode === "packages" ? "text-blue-600" : "text-gray-500 hover:text-blue-500"
                      }`}
                    onClick={() => {
                      setLabMode("packages");
                      if (!readOnly) cancelEdit();
                    }}
                  >
                    {readOnly ? "Packages" : "Add Packages"}
                  </button>
                </div>

                <div className="absolute bottom-0 left-0 flex w-full">
                  <span
                    className="block h-[2.5px] rounded bg-blue-600 transition-all duration-300"
                    style={{
                      position: "absolute",
                      width: "50%",
                      transition: "left 0.3s cubic-bezier(.4,0,.2,1)",
                      left: labMode === "tests" ? "0%" : "50%",
                    }}
                  />
                </div>
              </div>

              {labMode === "tests" && !readOnly && (
                <div className="w-full sm:w-auto min-w-0">
                  <Dropdown menu={importMenu} trigger={["click"]} placement="bottomRight">
                    <button
                      type="button"
                      className="w-full sm:w-auto min-w-0 flex items-center justify-between sm:justify-start gap-2 px-3 py-2 border border-gray-200 bg-white rounded-lg text-xs font-semibold text-gray-700 hover:bg-gray-50"
                    >
                      <span className="truncate">
                        {labImportMode === "bulk" ? "Import Bulk Tests" : "Enter Single Tests"}
                      </span>
                      <ChevronDown className="w-4 h-4 flex-none" />
                    </button>
                  </Dropdown>
                </div>
              )}
            </div>
          )}

          <h3 className="text-[15px] font-semibold text-[#2263E6] mb-2 px-4">
            {isNurse ? "Nursing Details" : `${activeSection} Details`}
          </h3>

          {/* ✅ FIX: was w-[98%]; that can create overflow with padding/margins in some layouts */}
          <div className="w-full max-w-full px-4 pb-4 min-w-0">
            {!readOnly && (
              <div className="border border-[#E5E9F5] bg-white rounded-xl px-5 pt-4 pb-5 mb-4 min-w-0 max-w-full overflow-hidden">
                {/* ---------------- LAB BULK IMPORT ---------------- */}
                {activeSection === "Laboratory" && labMode === "tests" && labImportMode === "bulk" ? (
                  <div className="w-full min-w-0 max-w-full">
                    <div className="text-sm font-semibold text-gray-900 mb-1">Import Bulk Tests</div>
                    <div className="text-xs text-gray-500 mb-4">
                      Download sample file for formatting. Upload Excel/CSV using the form below.
                    </div>

                    <div className="flex items-center gap-2 mb-4 flex-wrap">
                      <button
                        type="button"
                        className="px-3 py-2 text-xs font-semibold bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                        onClick={downloadLabTestsSample}
                      >
                        Download Sample Excel
                      </button>
                    </div>

                    {/* ✅ FIX: make this stack on small screens to avoid width increase */}
                    <div className="flex flex-col md:flex-row md:items-center md:justify-center gap-4 border-2 border-dashed border-[#CFE0FF] bg-[#F7FAFF] rounded-xl p-6 min-w-0 max-w-full">
                      <Upload {...bulkUploadProps} className="min-w-0 max-w-full">
                        <div className="flex flex-col items-center justify-center gap-2 text-center">
                          <UploadIcon className="w-7 h-7 text-blue-600" />
                          <div className="text-sm font-semibold text-gray-900">Choose a file or drag it here</div>
                          <div className="text-xs text-gray-500">Supported: .xlsx, .xls, .csv</div>
                          <button
                            type="button"
                            className="mt-2 px-4 py-2 text-xs font-semibold bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                          >
                            Upload File
                          </button>
                        </div>
                      </Upload>

                      {bulkFileList.length > 0 && (
                        <div className="bg-white border border-[#E5E9F5] rounded-lg p-3 min-w-0 max-w-full">
                          <div className="text-xs text-gray-700">
                            <div>
                              <b>File:</b> <span className="break-all">{bulkFileList[0]?.name}</span>
                            </div>
                            <div>
                              <b>Valid rows:</b> {bulkParsedRows.length}
                            </div>
                            <div>
                              <b>Errors:</b> {bulkParseErrors.length}
                            </div>
                          </div>

                          {bulkParseErrors.length > 0 && (
                            <div className="mt-2 max-h-28 overflow-auto text-xs text-red-600">
                              {bulkParseErrors.slice(0, 20).map((e, idx) => (
                                <div key={idx}>• {e}</div>
                              ))}
                              {bulkParseErrors.length > 20 && <div>...and {bulkParseErrors.length - 20} more</div>}
                            </div>
                          )}

                          <div className="mt-3 flex gap-2 flex-wrap">
                            <button
                              type="button"
                              className="px-3 py-2 text-xs font-semibold bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-60"
                              disabled={!bulkParsedRows.length}
                              onClick={handleBulkLabUpload}
                            >
                              Add to Pending Changes
                            </button>

                            <button
                              type="button"
                              className="px-3 py-2 text-xs font-semibold border border-gray-200 bg-white rounded-lg hover:bg-gray-50"
                              onClick={() => {
                                setBulkFileList([]);
                                setBulkParsedRows([]);
                                setBulkParseErrors([]);
                              }}
                            >
                              Clear
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                ) : null}

                {/* ---------------- LAB SINGLE TEST ENTRY ---------------- */}
                {activeSection === "Laboratory" && labMode === "tests" && labImportMode === "single" && (
                  <>
                    <div className="grid grid-cols-1 lg:grid-cols-12 gap-4 items-end w-full mb-6 min-w-0">
                      <div className={`${fieldWrap} lg:col-span-3 min-w-0`}>
                        <label className={labelCls}>Department</label>
                        <AutoComplete
                          className="w-full"
                          options={departmentOptions}
                          value={formDepartment || ""}
                          onChange={(val) => {
                            setFormDepartment(val);
                            setFormTestGroup("");
                            setFormParameterName("");
                            setFormSpecimentType("");
                            setFormUnit("mg/dl");
                          }}
                          onSelect={(val) => {
                            setFormDepartment(val);
                            setFormTestGroup("");
                            setFormParameterName("");
                            setFormSpecimentType("");
                            setFormUnit("mg/dl");
                          }}
                          placeholder="Select/Enter department"
                          filterOption={(input, option) =>
                            (option?.label ?? option?.value ?? "").toLowerCase().includes(input.toLowerCase())
                          }
                        >
                          <Input size="small" style={{ borderRadius: 8, height: 38 }} className="font-poppins" />
                        </AutoComplete>
                      </div>

                      <div className={`${fieldWrap} lg:col-span-3 min-w-0`}>
                        <label className={labelCls}>Test Group</label>
                        <AutoComplete
                          className="w-full"
                          options={testGroupOptions}
                          value={formTestGroup || ""}
                          onChange={(val) => {
                            setFormTestGroup(val);
                            setFormParameterName("");
                            setFormSpecimentType("");
                            setFormUnit("mg/dl");
                          }}
                          onSelect={(val) => {
                            setFormTestGroup(val);
                            setFormParameterName("");
                            setFormSpecimentType("");
                            setFormUnit("mg/dl");
                          }}
                          placeholder="Select/Enter test group"
                          disabled={!normalizeServiceName(formDepartment)}
                          filterOption={(input, option) =>
                            (option?.label ?? option?.value ?? "").toLowerCase().includes(input.toLowerCase())
                          }
                        >
                          <Input size="small" style={{ borderRadius: 8, height: 38 }} className="font-poppins" />
                        </AutoComplete>
                      </div>

                      <div className={`${fieldWrap} lg:col-span-3 min-w-0`}>
                        <label className={labelCls}>Parameter Name</label>
                        <AutoComplete
                          className="w-full"
                          options={parameterOptions}
                          value={formParameterName || ""}
                          onChange={setFormParameterName}
                          onSelect={(val) => setFormParameterName(val)}
                          placeholder="Select/Enter parameter"
                          disabled={!normalizeServiceName(formDepartment) || !normalizeServiceName(formTestGroup)}
                          filterOption={(input, option) =>
                            (option?.label ?? option?.value ?? "").toLowerCase().includes(input.toLowerCase())
                          }
                        >
                          <Input size="small" style={{ borderRadius: 8, height: 38 }} className="font-poppins" />
                        </AutoComplete>
                      </div>

                      <div className={`${fieldWrap} lg:col-span-3 min-w-0`}>
                        <label className={labelCls}>Specimen Type</label>
                        <Input
                          placeholder="Blood"
                          value={formSpecimentType}
                          onChange={(e) => setFormSpecimentType(e.target.value)}
                          size="small"
                          style={{ borderRadius: 8, height: 38 }}
                          className="font-poppins"
                        />
                      </div>
                    </div>

                    <div className="grid grid-cols-1 lg:grid-cols-12 gap-4 items-end w-full mb-4 min-w-0">
                      <div className={`${fieldWrap} lg:col-span-2 min-w-0`}>
                        <label className={labelCls}>Unit</label>
                        <Input
                          placeholder="mg/dl"
                          value={formUnit}
                          onChange={(e) => setFormUnit(e.target.value)}
                          size="small"
                          style={{ borderRadius: 8, height: 38 }}
                          className="font-poppins"
                        />
                      </div>

                      <div className={`${fieldWrap} lg:col-span-2 min-w-0`}>
                        <label className={labelCls}>Fee (in ₹)</label>
                        <InputNumber
                          placeholder="0"
                          value={formFee}
                          onChange={(v) => setFormFee(clampInt(v, { min: 0, max: MAX_FEE }))}
                          min={0}
                          max={MAX_FEE}
                          precision={0}
                          step={1}
                          controls={false}
                          size="small"
                          className="w-full p-2"
                          style={{ borderRadius: 8, height: 38 }}
                          parser={(val) => String(val ?? "").replace(/[^\d]/g, "")}
                          formatter={(val) => String(val ?? "").replace(/[^\d]/g, "")}
                        />
                      </div>

                      <div className="lg:col-span-3 min-w-0">{renderDiscountField()}</div>

                      <div className={`${fieldWrap} lg:col-span-2 min-w-0`}>
                        <label className={labelCls}>Fasting Required</label>
                        <div className="h-[38px] flex items-center gap-2">
                          <Switch checked={formFastingRequired} onChange={setFormFastingRequired} />
                          <span className="text-xs text-gray-600">{formFastingRequired ? "Yes" : "No"}</span>
                        </div>
                      </div>

                      <div className={`${fieldWrap} lg:col-span-2 min-w-0`}>
                        <label className={labelCls}>Prescription Required</label>
                        <div className="h-[38px] flex items-center gap-2">
                          <Switch checked={formPrescriptionRequired} onChange={setFormPrescriptionRequired} />
                          <span className="text-xs text-gray-600">{formPrescriptionRequired ? "Yes" : "No"}</span>
                        </div>
                      </div>

                      <div className="lg:col-span-1 flex justify-end items-end min-w-0">{renderActionButtons()}</div>
                    </div>

                    <div className="mt-1 w-full flex items-end gap-3 min-w-0">
                      <div className="flex-1 min-w-0">
                        <label className={labelCls}>Notes</label>
                        <Input
                          placeholder="Notes"
                          value={formNotes}
                          onChange={(e) => setFormNotes(e.target.value)}
                          size="small"
                          style={{ borderRadius: 8, height: 40 }}
                          className="font-poppins"
                        />
                      </div>
                    </div>
                  </>
                )}

                {/* ---------------- LAB PACKAGES ---------------- */}
                {activeSection === "Laboratory" && labMode === "packages" && (
                  <>
                    <div className="grid grid-cols-1 md:grid-cols-12 gap-4 mb-4 min-w-0">
                      <div className={`${fieldWrap} md:col-span-6 min-w-0`}>
                        <label className={labelCls}>Package Name</label>
                        <Input
                          placeholder="Enter package name"
                          value={formPackageName}
                          onChange={(e) => setFormPackageName(e.target.value)}
                          size="small"
                          style={{ borderRadius: 8, height: 38 }}
                          className="font-poppins"
                        />
                      </div>

                      <div className={`${fieldWrap} md:col-span-6 min-w-0`}>
                        <label className={labelCls}>Tests</label>
                        <Select
                          mode="tags"
                          placeholder="Select or type tests"
                          value={formPackageTests}
                          onChange={(vals) => setFormPackageTests(uniqTrimmed(vals))}
                          options={labTestOptionsForPackages}
                          size="small"
                          tokenSeparators={[","]}
                          maxTagCount="responsive"
                          style={{ borderRadius: 8, minHeight: 38, width: "100%" }}
                        />
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-12 gap-4 mb-4 min-w-0">
                      <div className={`${fieldWrap} md:col-span-3 min-w-0`}>
                        <label className={labelCls}>Fee (in ₹)</label>
                        <InputNumber
                          placeholder="0"
                          value={formFee}
                          onChange={(v) => setFormFee(clampInt(v, { min: 0, max: MAX_FEE }))}
                          min={0}
                          max={MAX_FEE}
                          precision={0}
                          step={1}
                          controls={false}
                          size="small"
                          className="w-full p-2"
                          style={{ borderRadius: 8, height: 38 }}
                          parser={(val) => String(val ?? "").replace(/[^\d]/g, "")}
                          formatter={(val) => String(val ?? "").replace(/[^\d]/g, "")}
                        />
                      </div>

                      <div className={`${fieldWrap} md:col-span-4 min-w-0`}>{renderDiscountField()}</div>

                      <div className={`${fieldWrap} md:col-span-2 min-w-0`}>
                        <label className={labelCls}>Fasting Required</label>
                        <div className="h-[38px] flex items-center gap-2">
                          <Switch checked={formFastingRequired} onChange={setFormFastingRequired} />
                          <span className="text-xs text-gray-600">{formFastingRequired ? "Yes" : "No"}</span>
                        </div>
                      </div>

                      <div className={`${fieldWrap} md:col-span-3 min-w-0`}>
                        <label className={labelCls}>Prescription Required</label>
                        <div className="h-[38px] flex items-center gap-2">
                          <Switch checked={formPrescriptionRequired} onChange={setFormPrescriptionRequired} />
                          <span className="text-xs text-gray-600">{formPrescriptionRequired ? "Yes" : "No"}</span>
                        </div>
                      </div>
                    </div>

                    <div className="flex items-end gap-3 min-w-0">
                      <div className="flex-1 min-w-0">
                        <label className={labelCls}>Notes</label>
                        <Input
                          placeholder="Notes"
                          value={formNotes}
                          onChange={(e) => setFormNotes(e.target.value)}
                          size="small"
                          style={{ borderRadius: 8, height: 40 }}
                          className="font-poppins"
                        />
                      </div>
                      <div className="flex-none pb-1">{renderActionButtons()}</div>
                    </div>
                  </>
                )}

                {/* ---------------- NURSE ONETIME ---------------- */}
                {isNurse && nurseMode === "onetime" && (
                  <>
                    <div className="grid grid-cols-1 md:grid-cols-12 gap-4 items-end w-full mb-3 min-w-0">
                      <div className={`${fieldWrap} md:col-span-6 min-w-0`}>
                        <div className="mb-1">{renderServiceLabelWithInfo()}</div>
                        {renderServiceInput()}
                      </div>

                      <div className={`${fieldWrap} md:col-span-2 mt-2 min-w-0`}>
                        <label className={labelCls}>Fee (in ₹)</label>
                        <InputNumber
                          placeholder="0"
                          value={formFee}
                          onChange={(v) => setFormFee(clampInt(v, { min: 0, max: MAX_FEE }))}
                          min={0}
                          max={MAX_FEE}
                          precision={0}
                          step={1}
                          className="w-full p-2"
                          controls={false}
                          size="small"
                          style={{ borderRadius: 8, height: 38 }}
                          parser={(val) => String(val ?? "").replace(/[^\d]/g, "")}
                          formatter={(val) => String(val ?? "").replace(/[^\d]/g, "")}
                        />
                      </div>

                      <div className="md:col-span-3 min-w-0 mt-2">{renderDiscountField()}</div>

                      <div className="md:col-span-1 flex justify-end items-end min-w-0">{renderActionButtons()}</div>
                    </div>

                    <div className="mt-1 w-full flex items-end gap-3 min-w-0">
                      <div className="flex-1 min-w-0">
                        <label className={labelCls}>Notes</label>
                        <Input
                          placeholder="Notes"
                          value={formNotes}
                          onChange={(e) => setFormNotes(e.target.value)}
                          size="small"
                          style={{ borderRadius: 8, height: 40 }}
                          className="font-poppins"
                        />
                      </div>
                    </div>
                  </>
                )}

                {/* ---------------- NURSE REGULAR ---------------- */}
                {isNurse && nurseMode === "regular" && (
                  <>
                    <div className="grid grid-cols-1 md:grid-cols-12 gap-4 mb-4 min-w-0">
                      <div className={`${fieldWrap} md:col-span-5 min-w-0`}>
                        <label className={labelCls}>Service Name</label>
                        <Input
                          placeholder="Enter service name"
                          value={formServiceName}
                          onChange={(e) => setFormServiceName(e.target.value)}
                          size="small"
                          style={{ borderRadius: 8, height: 38 }}
                          className="font-poppins text-start"
                        />
                      </div>

                      <div className={`${fieldWrap} md:col-span-3 min-w-0`}>
                        <label className={labelCls}>Role</label>
                        {lockNurseRegularPrefillFields ? (
                          <div className="h-[38px] px-3 rounded-lg border border-blue-200 bg-blue-50 text-blue-800 font-medium flex items-center">
                            {NURSE_ROLES.find((r) => r.value === formRole)?.label || "Nurse"}
                          </div>
                        ) : (
                          <Select
                            value={formRole || undefined}
                            onChange={setFormRole}
                            size="small"
                            style={{ borderRadius: 8, height: 38 }}
                            placeholder="Select role"
                          >
                            {NURSE_ROLES.map((r) => (
                              <Select.Option key={r.value} value={r.value}>
                                {r.label}
                              </Select.Option>
                            ))}
                          </Select>
                        )}
                      </div>

                      <div className={`${fieldWrap} md:col-span-4 min-w-0`}>
                        <label className={labelCls}>Shift & Duration</label>
                        {lockNurseRegularPrefillFields ? (
                          <div className="h-[38px] px-3 rounded-lg border border-blue-200 bg-blue-50 text-blue-800 font-medium flex items-center">
                            {SHIFT_DURATION_OPTIONS.find((opt) => opt.value === formShiftDuration)?.label || formShiftDuration || "-"}
                          </div>
                        ) : (
                          <Select
                            value={formShiftDuration || undefined}
                            onChange={(val) => setFormShiftDuration(val)}
                            size="small"
                            style={{ borderRadius: 8, height: 38 }}
                            placeholder="Select type"
                            disabled={Boolean(editingKey)}
                          >
                            {SHIFT_DURATION_OPTIONS.map((opt) => (
                              <Select.Option key={opt.value} value={opt.value}>
                                {opt.label}
                              </Select.Option>
                            ))}
                          </Select>
                        )}
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-12 gap-4 mb-4 min-w-0">
                      <div className={`${fieldWrap} md:col-span-12 min-w-0`}>
                        <label className={labelCls}>Services</label>
                        {lockNurseRegularPrefillFields ? (
                          <div className="min-h-[38px] px-2 py-1.5 rounded-lg border border-blue-200 bg-blue-50 flex flex-wrap gap-1.5 items-center">
                            {formServices.length > 0 ? (
                              formServices.map((service) => (
                                <span
                                  key={service}
                                  className="px-2 py-0.5 rounded-md bg-white border border-blue-100 text-blue-800 text-[12px]"
                                >
                                  {service}
                                </span>
                              ))
                            ) : (
                              <span className="text-blue-700 text-[12px]">No services</span>
                            )}
                          </div>
                        ) : (
                          <Select
                            mode="tags"
                            placeholder="Select services"
                            value={formServices}
                            onChange={(vals) => setFormServices(uniqTrimmed(vals))}
                            options={regularServicesOptions}
                            size="small"
                            tokenSeparators={[","]}
                            maxTagCount="responsive"
                            style={{ borderRadius: 8, minHeight: 38, width: "100%" }}
                          />
                        )}
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-12 gap-4 mb-4 min-w-0">
                      <div className={`${fieldWrap} md:col-span-3 min-w-0`}>
                        <label className={labelCls}>Fee (in ₹)</label>
                        <InputNumber
                          placeholder="0"
                          value={formFee}
                          onChange={(v) => setFormFee(clampInt(v, { min: 0, max: MAX_FEE }))}
                          min={0}
                          max={MAX_FEE}
                          precision={0}
                          step={1}
                          controls={false}
                          size="small"
                          className="w-full p-2"
                          style={{ borderRadius: 8, height: 38 }}
                          parser={(val) => String(val ?? "").replace(/[^\d]/g, "")}
                          formatter={(val) => String(val ?? "").replace(/[^\d]/g, "")}
                        />
                      </div>

                      <div className={`${fieldWrap} md:col-span-4 min-w-0`}>{renderDiscountField()}</div>
                    </div>

                    <div className="flex items-end gap-3 min-w-0">
                      <div className="flex-1 min-w-0">
                        <label className={labelCls}>Notes</label>
                        <Input
                          placeholder="Notes"
                          value={formNotes}
                          onChange={(e) => setFormNotes(e.target.value)}
                          size="small"
                          style={{ borderRadius: 8, height: 40 }}
                          className="font-poppins"
                        />
                      </div>
                      <div className="flex-none pb-1">{renderActionButtons()}</div>
                    </div>
                  </>
                )}

                {/* ---------------- PHYSIO ---------------- */}
                {activeSection === "Physiotherapist" && (
                  <>
                    <div className="grid grid-cols-1 md:grid-cols-12 gap-4 mb-4 min-w-0">
                      <div className={`${fieldWrap} md:col-span-6 min-w-0`}>
                        <label className={labelCls}>Service</label>
                        {renderServiceInput()}
                      </div>

                      <div className={`${fieldWrap} md:col-span-3 min-w-0`}>
                        <label className={labelCls}>Fee (in ₹)</label>
                        <InputNumber
                          placeholder="0"
                          value={formFee}
                          onChange={(v) => setFormFee(clampInt(v, { min: 0, max: MAX_FEE }))}
                          min={0}
                          max={MAX_FEE}
                          precision={0}
                          step={1}
                          className="w-full text-center p-2"
                          controls={false}
                          size="small"
                          style={{ borderRadius: 8, height: 38 }}
                          parser={(val) => String(val ?? "").replace(/[^\d]/g, "")}
                          formatter={(val) => String(val ?? "").replace(/[^\d]/g, "")}
                        />
                      </div>

                      <div className="md:col-span-3 min-w-0">{renderDiscountField()}</div>
                    </div>

                    <div className="flex items-end gap-3 min-w-0">
                      <div className="flex-1 min-w-0">
                        <label className={labelCls}>Notes</label>
                        <Input
                          placeholder="Notes"
                          value={formNotes}
                          onChange={(e) => setFormNotes(e.target.value)}
                          size="small"
                          style={{ borderRadius: 8, height: 40 }}
                          className="font-poppins"
                        />
                      </div>
                      <div className="flex-none pb-1">{renderActionButtons()}</div>
                    </div>
                  </>
                )}
              </div>
            )}

            {/* ---------------- PENDING CHANGES ---------------- */}
            {!readOnly && (
              <>
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 mb-2 mt-2 min-w-0">
                  <div className="min-w-0">
                    <div className="text-sm font-semibold text-gray-900">Pending Changes</div>
                    <div className="text-xs text-gray-500 mt-0.5">New / updated / removed items will appear here.</div>
                  </div>

                  {hasDraftChanges && (
                    <button
                      className="w-full sm:w-auto px-4 py-1.5 bg-blue-600 text-white rounded-sm text-xs font-semibold hover:bg-blue-700 disabled:opacity-60"
                      onClick={handleSaveSection}
                      disabled={saving}
                      type="button"
                    >
                      {saving ? "Saving..." : "Save Changes"}
                    </button>
                  )}
                </div>

                {hasDraftChanges ? (
                  <div className="w-full max-w-full overflow-x-auto mb-4 min-w-0">
                    <Table
                      columns={draftColumns}
                      dataSource={draftActiveData}
                      pagination={false}
                      rowKey="key"
                      scroll={{ x: tableScrollX }}
                      tableLayout="fixed"
                      locale={{ emptyText: "No pending changes" }}
                      size="small"
                      className="rounded-xl overflow-hidden border border-[#E5E9F5]"
                    />
                  </div>
                ) : (
                  <div className="text-xs text-gray-500 bg-white border border-[#E5E9F5] rounded-lg p-3 mb-4">
                    No pending changes. (Everything is already saved.)
                  </div>
                )}
              </>
            )}

            {/* ---------------- SAVED PRICING ---------------- */}
            <div className="mb-2 mt-2">
              <div className="text-sm font-semibold text-gray-900">Saved Pricing</div>
            </div>

            <div className="w-full max-w-full overflow-x-auto mb-2 min-w-0">
              <Table
                columns={savedColumns}
                dataSource={savedActiveData}
                pagination={false}
                rowKey="key"
                scroll={{ x: tableScrollX }}
                tableLayout="fixed"
                locale={{ emptyText: "No saved pricing" }}
                size="small"
                className="rounded-xl overflow-hidden border border-[#E5E9F5]"
              />
            </div>
          </div>
        </div>

        {!readOnly && (
          <div className="flex items-center justify-end gap-3 border-t border-gray-200 bg-white px-4 py-3 flex-none">
            <button
              type="button"
              onClick={() => navigate(-1)}
              className="px-8 py-1.5 bg-white border border-gray-300 hover:bg-gray-50 text-gray-700 rounded-lg text-sm font-medium"
            >
              Back
            </button>
          </div>
        )}
      </div>
    </div >

  );
};

export default Pricing;
